import { useState, useEffect, useMemo } from 'react';
import { Node, Edge } from 'reactflow';
import {
  X,
  Trash2,
  Play,
  Loader2,
  Info,
  Settings,
  ArrowRightFromLine,
  Copy,
  Check,
  Zap,
  Clock,
  Globe,
  Phone,
  PhoneOff,
  PhoneIncoming,
  Voicemail,
  MessageSquare,
  MessageCircle,
  UserPlus,
  GitBranch,
  UserCheck,
  FileText,
  Tag,
  HelpCircle,
  ChevronRight,
  Calendar,
  Mail,
  User,
  Webhook,
  Megaphone,
  Database,
  Volume2,
  SlidersHorizontal,
  Bot,
  Mic,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-frontend/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-frontend/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@easyflow/ui-frontend/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@easyflow/ui-frontend/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import { KnowledgeBaseManager } from '@/components/ai/KnowledgeBaseManager';
import { LLMProviderConfig } from '@/components/ai/LLMProviderConfig';
import { useChatModelOptions, type ChatModelEntry as LLMModelEntry } from '@/hooks/useChatModelOptions';
import apiClient from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import { TRIGGER_CONFIG_COMPONENTS } from './TriggerConfigSections';
import { FLOW_CONTROL_CONFIG_COMPONENTS } from './FlowControlConfigSections';
import { ConfigFieldWithMode, FieldMode, FieldOption, AvailableVariable as ConfigAvailableVariable } from './ConfigFieldWithMode';
import type { MessagingWorkflowTemplateOption } from '@easyflow/shared';

interface NodeDefinition {
  id: string;
  display_name: string;
  description: string;
  category: string;
  icon: string;
  input_schema: NodeSchema;
  output_schema: NodeSchema;
  // Present on MCP-derived node definitions; used to match a tool by name.
  mcp_tool_name?: string;
}

// Result of a node test/simulate run (dynamic output/error payloads).
interface NodeTestResult {
  success?: boolean;
  timestamp?: string;
  output?: unknown;
  error?: unknown;
}

// JSON-schema field fragment: only the properties the FE reads/writes are
// typed; the schema stays permissive via the index signature.
interface FieldSchema {
  type?: string;
  format?: string;
  key?: string;
  context?: string;
  default?: unknown;
  description?: string;
  placeholder?: string;
  title?: string;
  enum?: string[];
  optionsSource?: string;
  allLabel?: string;
  allowAll?: boolean;
  properties?: Record<string, FieldSchema>;
  [key: string]: unknown;
}

// Node input/output JSON schema as read by the config UI.
interface NodeSchema {
  properties?: Record<string, FieldSchema>;
  required?: string[];
  [key: string]: unknown;
}

interface AvailableVariable {
  path: string;
  type: string;
  context?: string; // Semantic context (phone_number, trunk_id, email, etc.)
  source: string;
  source_node_id?: string;
  source_node_label?: string;
  description?: string;
}

interface NodeConfigModalProps {
  open: boolean;
  node: Node | null;
  edges: Edge[];
  availableNodes: Record<string, NodeDefinition[]>;
  availableVariables: AvailableVariable[];
  onUpdateConfig: (config: Record<string, unknown>) => void;
  onUpdateLabel: (label: string) => void;
  onDelete: () => void;
  onClose: () => void;
  onTestPreviousNode?: (nodeId: string) => Promise<unknown>;
}

const TRIGGER_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  trigger_manual: Play,
  trigger_scheduled: Clock,
  trigger_webhook: Globe,
  trigger_call_hangup: PhoneOff,
  trigger_call_answered: PhoneIncoming,
  trigger_voicemail_left: Voicemail,
  trigger_sms_received: MessageSquare,
  trigger_whatsapp_received: MessageCircle,
  trigger_lead_created: UserPlus,
  trigger_lead_stage_changed: GitBranch,
  trigger_customer_created: UserCheck,
  trigger_form_submitted: FileText,
  trigger_tag_added: Tag,
  trigger_message_delivered: MessageSquare,
  trigger_message_failed: MessageSquare,
  trigger_message_read: MessageSquare,
  trigger_contact_status_changed: GitBranch,
  trigger_time_in_status: Clock,
  trigger_appointment_created: Calendar,
  trigger_appointment_reminder: Clock,
  trigger_campaign_event: Megaphone,
};

const TRIGGER_COLORS: Record<string, string> = {
  trigger_manual: 'bg-emerald-500 dark:bg-emerald-600',
  trigger_scheduled: 'bg-blue-500 dark:bg-blue-600',
  trigger_webhook: 'bg-purple-500 dark:bg-purple-600',
  trigger_call_hangup: 'bg-red-500 dark:bg-red-600',
  trigger_call_answered: 'bg-green-500 dark:bg-green-600',
  trigger_voicemail_left: 'bg-orange-500 dark:bg-orange-600',
  trigger_sms_received: 'bg-cyan-500 dark:bg-cyan-600',
  trigger_whatsapp_received: 'bg-green-600 dark:bg-green-700',
  trigger_lead_created: 'bg-indigo-500 dark:bg-indigo-600',
  trigger_lead_stage_changed: 'bg-violet-500 dark:bg-violet-600',
  trigger_customer_created: 'bg-teal-500 dark:bg-teal-600',
  trigger_form_submitted: 'bg-amber-500 dark:bg-amber-600',
  trigger_tag_added: 'bg-pink-500 dark:bg-pink-600',
  trigger_message_delivered: 'bg-blue-500 dark:bg-blue-600',
  trigger_message_failed: 'bg-red-500 dark:bg-red-600',
  trigger_message_read: 'bg-sky-500 dark:bg-sky-600',
  trigger_contact_status_changed: 'bg-fuchsia-500 dark:bg-fuchsia-600',
  trigger_time_in_status: 'bg-amber-500 dark:bg-amber-600',
  trigger_appointment_created: 'bg-teal-500 dark:bg-teal-600',
  trigger_appointment_reminder: 'bg-teal-500 dark:bg-teal-600',
  trigger_campaign_event: 'bg-purple-500 dark:bg-purple-600',
};

const NODE_CATEGORY_COLORS: Record<string, string> = {
  triggers: 'bg-cyan-500 dark:bg-cyan-600',
  flow_control: 'bg-purple-500 dark:bg-purple-600',
  utilities: 'bg-green-500 dark:bg-green-600',
  ai: 'bg-violet-500 dark:bg-violet-600',
  mcp: 'bg-amber-500 dark:bg-amber-600',
};

// Prompt textareas share the same shell: inset two-pixel ring in the workflow accent.
const PROMPT_TEXTAREA_CLASS =
  'rounded-md border border-violet-200 dark:border-violet-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-inset focus:ring-violet-400 resize-y';

export function NodeConfigModal({
  open,
  node,
  edges,
  availableNodes,
  availableVariables,
  onUpdateConfig,
  onUpdateLabel,
  onDelete,
  onClose,
  onTestPreviousNode,
}: NodeConfigModalProps) {
  const { toast } = useToast();
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [label, setLabel] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<NodeTestResult | null>(null);
  const REALTIME_FALLBACK = useMemo(() => ([
    { id: 'gpt-realtime-2', name: 'GPT Realtime 2 (Consigliato)' },
    { id: 'gpt-realtime-translate', name: 'GPT Realtime Translate' },
    { id: 'gpt-realtime-whisper', name: 'GPT Realtime Whisper' },
    { id: 'gpt-realtime', name: 'GPT Realtime (GA)' },
    { id: 'gpt-realtime-mini', name: 'GPT Realtime Mini' },
    { id: 'gpt-4o-realtime-preview', name: 'GPT-4o Preview (Deprecato)' },
  ]), []);
  const GEMINI_VOICE_FALLBACK = useMemo(() => ([
    { id: 'gemini-2.5-flash-native-audio-latest', name: 'Gemini 2.5 Flash Native Audio (Latest)' },
    { id: 'gemini-2.5-flash-native-audio-preview-12-2025', name: 'Gemini 2.5 Flash Native Audio' },
    { id: 'gemini-2.0-flash-live-001', name: 'Gemini 2.0 Flash Live' },
  ]), []);

  // Centralized model fetch via React Query hook (Task #1066)
  const realtimeQuery = useChatModelOptions('openai-realtime', { enabled: open });
  const geminiVoiceQuery = useChatModelOptions('gemini-voice', { enabled: open });

  const mapVoiceModels = (list: LLMModelEntry[] | undefined, fallback: {id:string;name:string}[]) => {
    if (!list || list.length === 0) return fallback;
    const mapped = list
      .filter((m) => m.status !== 'deprecated')
      .map((m) => ({
        id: m.id,
        name: `${m.displayName || m.name || m.id}${m.isRecommended ? ' (Consigliato)' : ''}`,
      }));
    return mapped.length > 0 ? mapped : fallback;
  };

  const realtimeModels = useMemo(() => mapVoiceModels(realtimeQuery.data?.models, REALTIME_FALLBACK), [realtimeQuery.data, REALTIME_FALLBACK]);
  const geminiVoiceModels = useMemo(() => mapVoiceModels(geminiVoiceQuery.data?.models, GEMINI_VOICE_FALLBACK), [geminiVoiceQuery.data, GEMINI_VOICE_FALLBACK]);
  const realtimeModelsOffline = realtimeQuery.data?.source === 'fallback' || realtimeQuery.isError;
  const geminiVoiceModelsOffline = geminiVoiceQuery.data?.source === 'fallback' || geminiVoiceQuery.isError;

  // Extract config from various possible locations in node.data
  const extractConfig = (data: Record<string, unknown> | null | undefined): Record<string, unknown> => {
    if (!data) return {};
    // Priority: data.config > data.params > direct fields in data
    const dataConfig = data.config as Record<string, unknown> | undefined;
    if (dataConfig && Object.keys(dataConfig).length > 0) {
      return dataConfig;
    }
    const dataParams = data.params as Record<string, unknown> | undefined;
    if (dataParams && Object.keys(dataParams).length > 0) {
      return dataParams;
    }
    // Extract known config fields directly from data (for legacy/trigger nodes)
    const knownConfigFields = [
      'trunk_id', 'direction', 'destination', 'caller_filter', 'min_duration',
      'voicemail_box_id', 'phone_number_id', 'keyword_filter',
      'To', 'From', 'ContentSid', 'Body', 'MediaUrl',
      'schedule_type', 'cron_expression', 'interval_minutes', 'run_at_time',
      'pipeline_id', 'stage_id', 'lead_source', 'form_id', 'tag_id', 'entity_type'
    ];
    const directConfig: Record<string, unknown> = {};
    for (const field of knownConfigFields) {
      if (data[field] !== undefined) {
        directConfig[field] = data[field];
      }
    }
    return directConfig;
  };

  const nodeDefinition = useMemo(() => {
    if (!node) return null;
    // Search in available nodes first
    for (const category of Object.values(availableNodes)) {
      const found = category.find((n) => n.id === node.data.nodeType);
      if (found) return found;
    }
    // Fallback for MCP nodes: create definition from node data + try to find schema from availableNodes by tool name
    if (node.data.category?.startsWith('mcp:') || node.data.mcp_server_name) {
      // Try to find input_schema from availableNodes by matching tool name suffix
      let inputSchema = node.data.input_schema;
      if (!inputSchema || Object.keys(inputSchema?.properties || {}).length === 0) {
        // Search by partial match (tool name might be stored differently)
        const nodeTypeParts = node.data.nodeType?.split('_') || [];
        const toolName = nodeTypeParts.slice(2).join('_'); // Skip mcp_{server_id}_
        for (const category of Object.values(availableNodes)) {
          for (const n of category) {
            const mcpToolName = n.mcp_tool_name;
            if (n.id === node.data.nodeType || 
                (mcpToolName && mcpToolName === toolName) ||
                n.id?.endsWith(`_${toolName}`)) {
              inputSchema = n.input_schema;
              break;
            }
          }
          if (inputSchema && Object.keys(inputSchema?.properties || {}).length > 0) break;
        }
      }
      return {
        id: node.data.nodeType,
        display_name: node.data.label || 'MCP Tool',
        description: `Tool from ${node.data.mcp_server_name || 'MCP Server'}`,
        category: node.data.category || `mcp:${node.data.mcp_server_name}`,
        icon: 'server',
        input_schema: inputSchema || { type: 'object', properties: {} },
        output_schema: { type: 'object', properties: { result: { type: 'object', description: 'Tool execution result' } } },
        type: 'mcp_tool',
        source: 'mcp',
        mcp_server_name: node.data.mcp_server_name
      };
    }
    return null;
  }, [availableNodes, node]);

  useEffect(() => {
    if (node) {
      setConfig(extractConfig(node.data));
      setLabel(node.data.label || nodeDefinition?.display_name || '');
      setTestResult(null);
    }
  }, [node?.id, node?.data, nodeDefinition, node]);

  const handleConfigChange = (key: string, value: unknown) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    onUpdateConfig(newConfig);
  };

  const handleLabelChange = (newLabel: string) => {
    setLabel(newLabel);
    onUpdateLabel(newLabel);
  };

  const handleSimulate = async () => {
    if (!node) return;
    try {
      setTesting(true);
      setTestResult(null);
      
      const mockData = generateMockOutput(node.data.nodeType);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setTestResult({
        success: true,
        timestamp: new Date().toISOString(),
        output: mockData
      });
      
      toast({ title: 'Simulazione completata', description: 'Output generato con dati di esempio' });
    } catch (error) {
      toast({
        title: 'Errore simulazione',
        description: 'Impossibile simulare l\'esecuzione',
        variant: 'destructive',
      });
    } finally {
      setTesting(false);
    }
  };

  if (!node || !nodeDefinition) return null;

  const nodeType = node.data.nodeType;
  const TriggerIcon = TRIGGER_ICONS[nodeType] || Zap;
  const triggerColor = TRIGGER_COLORS[nodeType] || 'bg-primary';
  const nodeCategory = nodeDefinition.category || 'triggers';
  const categoryColor = NODE_CATEGORY_COLORS[nodeCategory] || NODE_CATEGORY_COLORS[nodeCategory.toLowerCase()] || triggerColor;
  
  const incomingEdges = edges.filter(e => e.target === node.id);
  const hasIncomingConnections = incomingEdges.length > 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[min(95vw,1400px)] sm:max-w-[min(95vw,1400px)] max-h-[85vh] p-0 gap-0 overflow-hidden" aria-describedby="node-config-desc">
        <DialogHeader className="sr-only">
          <DialogTitle>Configura {label}</DialogTitle>
          <p id="node-config-desc" className="sr-only">Configurazione del nodo {label} nel workflow</p>
        </DialogHeader>
        
        <div className={`grid ${hasIncomingConnections ? 'grid-cols-[260px_minmax(0,1fr)_260px]' : 'grid-cols-[minmax(0,1fr)_260px]'} h-[75vh] divide-x overflow-hidden`}>
          {/* INPUT SECTION - Left Column (solo se ci sono connessioni in ingresso) */}
          {hasIncomingConnections && (
            <div className="flex flex-col bg-muted/20 min-h-0">
              <div className="px-4 py-3 border-b bg-muted/40">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <ArrowRightFromLine className="h-4 w-4 rotate-180" />
                  Input
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Variabili disponibili dai nodi precedenti
                </p>
              </div>
              <ScrollArea className="flex-1">
                <InputSection
                  availableVariables={availableVariables}
                  incomingEdges={incomingEdges}
                  onTestPreviousNode={onTestPreviousNode}
                />
              </ScrollArea>
            </div>
          )}

          {/* CONFIG SECTION - Center Column */}
          <div className="flex flex-col min-h-0">
            <div className="px-4 py-3 border-b bg-muted/30 flex items-center gap-3">
              <div className={`p-2 rounded-lg ${categoryColor} text-white shadow`}>
                <TriggerIcon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Input
                    value={label}
                    onChange={(e) => handleLabelChange(e.target.value)}
                    className="text-base font-semibold border-0 bg-transparent p-0 h-auto"
                    placeholder="Nome del nodo"
                  />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="text-muted-foreground hover:text-foreground transition-colors">
                          <Info className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs">
                        <p className="font-medium mb-1">{nodeDefinition.display_name}</p>
                        <p className="text-xs text-muted-foreground">{getTriggerExplanation(nodeType)}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-xs text-muted-foreground">{nodeDefinition.description}</p>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onDelete}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Elimina nodo</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 min-w-0">
              <div className="p-4 pb-8 min-w-0">
                <ConfigTab 
                  nodeType={nodeType}
                  config={config}
                  nodeDefinition={nodeDefinition}
                  availableVariables={availableVariables}
                  onConfigChange={handleConfigChange}
                  nodeId={node.id}
                  nodeLabel={node.data?.label || node.id}
                  realtimeModels={realtimeModels}
                  realtimeModelsOffline={realtimeModelsOffline}
                  geminiVoiceModels={geminiVoiceModels}
                  geminiVoiceModelsOffline={geminiVoiceModelsOffline}
                />
              </div>
            </div>
          </div>

          {/* OUTPUT SECTION - Right Column */}
          <div className="flex flex-col bg-muted/20 min-h-0">
            <div className="px-4 py-3 border-b bg-muted/40">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <ArrowRightFromLine className="h-4 w-4" />
                  Output
                </h3>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Cosa produce questo nodo
              </p>
            </div>
            <div className="p-3 border-b">
              <Button onClick={handleSimulate} disabled={testing} size="sm" className="w-full gap-2">
                {testing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Esecuzione test...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Esegui Test
                  </>
                )}
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <OutputSection
                nodeType={nodeType}
                nodeDefinition={nodeDefinition}
                nodeId={node.id}
                nodeLabel={node.data?.label || node.id}
                config={config}
                testResult={testResult}
              />
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const getNodeIconForInput = (nodeType: string) => {
  if (nodeType.includes('call')) return Phone;
  if (nodeType.includes('scheduled') || nodeType.includes('schedule')) return Calendar;
  if (nodeType.includes('email') || nodeType.includes('mail')) return Mail;
  if (nodeType.includes('sms') || nodeType.includes('whatsapp') || nodeType.includes('message')) return MessageSquare;
  if (nodeType.includes('lead') || nodeType.includes('customer') || nodeType.includes('user')) return User;
  if (nodeType.includes('tag')) return Tag;
  if (nodeType.includes('form')) return FileText;
  if (nodeType.includes('webhook')) return Webhook;
  if (nodeType.includes('manual')) return Play;
  if (nodeType.includes('voicemail')) return Voicemail;
  return Settings;
};

const getTypeColor = (type: string) => {
  switch (type) {
    case 'string': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
    case 'number': return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
    case 'boolean': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300';
    case 'date': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300';
    case 'object': return 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300';
    case 'array': return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300';
    default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }
};

const getContextLabel = (context: string | undefined): string => {
  if (!context) return '';
  const labels: Record<string, string> = {
    phone_number: 'telefono',
    phone_pattern: 'pattern tel.',
    email: 'email',
    contact_name: 'nome contatto',
    first_name: 'nome',
    last_name: 'cognome',
    trunk_id: 'ID trunk',
    trunk_name: 'nome trunk',
    call_id: 'ID chiamata',
    call_direction: 'direzione',
    call_disposition: 'esito',
    duration_seconds: 'durata (sec)',
    datetime: 'data/ora',
    message_id: 'ID messaggio',
    message_content: 'contenuto msg',
    conversation_id: 'ID convers.',
    lead_id: 'ID lead',
    lead_title: 'titolo lead',
    customer_id: 'ID cliente',
    campaign_id: 'ID campagna',
    stage_id: 'ID stage',
    stage_name: 'nome stage',
    form_id: 'ID form',
    form_name: 'nome form',
    tag_name: 'nome tag',
    user_id: 'ID utente',
    user_name: 'nome utente',
    url: 'URL',
    file_path: 'percorso file',
    generic_string: 'testo',
    generic_number: 'numero',
    generic_object: 'oggetto',
    boolean_flag: 'flag',
    extension_number: 'interno',
    voicemail_id: 'ID voicemail',
    person_id: 'ID persona',
    company_id: 'ID azienda',
    notes: 'note',
    monetary_value: 'valore €',
    percentage: 'percentuale',
    priority_level: 'priorità',
    utm_source: 'UTM source',
    utm_campaign: 'UTM campaign',
    ip_address: 'IP',
    http_headers: 'headers HTTP',
    http_method: 'metodo HTTP',
  };
  return labels[context] || context.replace(/_/g, ' ');
};

function InputSection({
  availableVariables,
  incomingEdges,
  onTestPreviousNode,
}: {
  availableVariables: AvailableVariable[];
  incomingEdges: Edge[];
  onTestPreviousNode?: (nodeId: string) => Promise<unknown>;
}) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [copiedPath, setCopiedPath] = useState<string | null>(null);
  const [testingNodeId, setTestingNodeId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, Record<string, unknown>>>({});

  useEffect(() => {
    const allGroupKeys = new Set(availableVariables.map(v => v.source_node_id || v.source));
    setExpandedGroups(allGroupKeys);
  }, [availableVariables]);

  const groupedVariables = useMemo(() => {
    const groups: Record<string, { 
      label: string; 
      source: string; 
      nodeId: string;
      nodeType: string;
      variables: AvailableVariable[] 
    }> = {};
    
    availableVariables.forEach(v => {
      const groupKey = v.source_node_id || v.source;
      if (!groups[groupKey]) {
        groups[groupKey] = {
          label: v.source_node_label || (v.source === 'trigger' ? 'Trigger' : v.source === 'system' ? 'Sistema' : 'Nodo'),
          source: v.source,
          nodeId: v.source_node_id || '',
          nodeType: v.source_node_id || v.source,
          variables: []
        };
      }
      groups[groupKey].variables.push(v);
    });
    
    return groups;
  }, [availableVariables]);

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const copyToClipboard = (path: string) => {
    navigator.clipboard.writeText(`{{${path}}}`);
    setCopiedPath(path);
    setTimeout(() => setCopiedPath(null), 1500);
  };

  const handleTestNode = async (nodeId: string) => {
    if (!onTestPreviousNode || !nodeId) return;
    
    setTestingNodeId(nodeId);
    try {
      const result = await onTestPreviousNode(nodeId);
      setTestResults(prev => ({ ...prev, [nodeId]: (result || {}) as Record<string, unknown> }));
    } catch (error) {
      console.error('Error testing node:', error);
    } finally {
      setTestingNodeId(null);
    }
  };

  return (
    <div className="p-2 space-y-2">
      {Object.entries(groupedVariables).map(([groupKey, group]) => {
        const IconComponent = getNodeIconForInput(group.nodeType);
        const isExpanded = expandedGroups.has(groupKey);
        const isTriggerGroup = group.source === 'trigger';
        const hasTestResult = testResults[group.nodeId];
        const isTesting = testingNodeId === group.nodeId;
        
        return (
          <div key={groupKey} className="rounded-lg border border-muted overflow-hidden">
            <div className={`
              flex items-center justify-between p-2 transition-colors
              ${isTriggerGroup ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-muted/30'}
            `}>
              <button 
                onClick={() => toggleGroup(groupKey)}
                className="flex items-center gap-2 flex-1 text-left"
              >
                <div className={`
                  p-1 rounded
                  ${isTriggerGroup ? 'bg-purple-100 dark:bg-purple-900/40' : 'bg-muted'}
                `}>
                  <IconComponent className={`
                    w-3.5 h-3.5
                    ${isTriggerGroup ? 'text-purple-600 dark:text-purple-400' : 'text-muted-foreground'}
                  `} />
                </div>
                <span className="text-xs font-medium text-foreground">
                  {group.label}
                </span>
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                  {group.variables.length}
                </Badge>
                <ChevronRight className={`
                  w-4 h-4 text-muted-foreground transition-transform
                  ${isExpanded ? 'rotate-90' : ''}
                `} />
              </button>
              
              {onTestPreviousNode && group.nodeId && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={isTesting}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTestNode(group.nodeId);
                        }}
                      >
                        {isTesting ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Play className="h-3 w-3" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Esegui nodo per vedere output reale
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            
            {isExpanded && (
              <div className="border-t border-muted">
                <div className="space-y-0.5 p-2">
                  {group.variables.map((variable, idx) => {
                    const shortName = variable.path.split('.').pop() || variable.path;
                    const testValue = hasTestResult ? testResults[group.nodeId]?.[shortName] : null;
                    
                    return (
                      <div
                        key={`${variable.path}-${idx}`}
                        className="group flex items-start justify-between p-1.5 rounded hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => copyToClipboard(variable.path)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <code className="text-xs font-mono text-purple-600 dark:text-purple-400">
                              {shortName}
                            </code>
                            <Badge className={`text-[9px] px-1 py-0 h-4 ${getTypeColor(variable.type)}`}>
                              {variable.type}
                            </Badge>
                            {variable.context && (
                              <Badge className="text-[8px] px-1 py-0 h-3.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                                {getContextLabel(variable.context)}
                              </Badge>
                            )}
                          </div>
                          {variable.description && (
                            <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                              {variable.description}
                            </p>
                          )}
                          {testValue !== undefined && testValue !== null && (
                            <p className="text-[10px] text-green-600 dark:text-green-400 mt-0.5 font-mono truncate">
                              = {typeof testValue === 'object' ? JSON.stringify(testValue) : String(testValue)}
                            </p>
                          )}
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2 mt-0.5">
                          {copiedPath === variable.path ? (
                            <Check className="w-3.5 h-3.5 text-green-500" />
                          ) : (
                            <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function OutputSection({
  nodeType,
  nodeDefinition,
  nodeId,
  nodeLabel,
  config,
  testResult,
}: {
  nodeType: string;
  nodeDefinition: NodeDefinition | null;
  nodeId: string;
  nodeLabel: string;
  config: Record<string, unknown>;
  testResult: NodeTestResult | null;
}) {
  const hasTestResults = testResult?.output !== undefined;
  const hasError = testResult?.error !== undefined;

  const formatValue = (value: unknown, indent: number = 0): JSX.Element => {
    if (value === undefined) return <span className="text-gray-400 italic">undefined</span>;
    if (value === null) return <span className="text-gray-400 italic">null</span>;
    if (value === '') return <span className="text-amber-500 italic">(vuoto)</span>;
    if (typeof value === 'boolean') return <span className="text-indigo-600 dark:text-indigo-400">{value ? 'true' : 'false'}</span>;
    if (typeof value === 'number') return <span className="text-blue-600 dark:text-blue-400">{value}</span>;
    if (typeof value === 'string') {
      if (value.length > 200) return <span className="text-green-700 dark:text-green-400">"{value.substring(0, 200)}..."</span>;
      return <span className="text-green-700 dark:text-green-400">"{value}"</span>;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="text-gray-500">[]</span>;
      return (
        <div className="ml-2">
          <span className="text-gray-500">[</span>
          {value.slice(0, 5).map((item, i) => (
            <div key={i} className="ml-2">
              {formatValue(item, indent + 1)}
              {i < Math.min(value.length, 5) - 1 && <span className="text-gray-500">,</span>}
            </div>
          ))}
          {value.length > 5 && <div className="ml-2 text-gray-400 italic">...e altri {value.length - 5} elementi</div>}
          <span className="text-gray-500">]</span>
        </div>
      );
    }
    if (typeof value === 'object') {
      const objValue = value as Record<string, unknown>;
      const keys = Object.keys(objValue);
      if (keys.length === 0) return <span className="text-gray-500">{'{}'}</span>;
      return (
        <div className="ml-2">
          {keys.slice(0, 10).map((key, i) => (
            <div key={key} className="flex items-start gap-1">
              <span className="text-purple-600 dark:text-purple-400 font-medium">{key}:</span>
              {formatValue(objValue[key], indent + 1)}
            </div>
          ))}
          {keys.length > 10 && <div className="text-gray-400 italic">...e altre {keys.length - 10} proprietà</div>}
        </div>
      );
    }
    return <span>{String(value)}</span>;
  };

  return (
    <div className="p-3 space-y-4">
      {/* PRE-TEST: Solo messaggio di attesa */}
      {!hasTestResults && !hasError && (
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
            <Play className="h-8 w-8 text-muted-foreground/50" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">Nessun risultato</p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Clicca "Esegui Test" per vedere l'output del nodo
          </p>
        </div>
      )}

      {/* POST-TEST ERROR: Mostra errore */}
      {hasError && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 px-1">
            <X className="h-4 w-4 text-red-500" />
            <p className="text-sm font-semibold text-red-700 dark:text-red-400">Errore Esecuzione</p>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 p-3">
            <pre className="text-xs font-mono text-red-700 dark:text-red-300 whitespace-pre-wrap break-words">
              {typeof testResult.error === 'string' ? testResult.error : JSON.stringify(testResult.error, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* POST-TEST SUCCESS: Mostra risultati reali */}
      {hasTestResults && (
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-500" />
              <p className="text-sm font-semibold text-green-700 dark:text-green-400">
                Output da "{nodeLabel}"
              </p>
            </div>
            <Badge className="text-[10px] h-5 px-2 bg-green-500 text-white">
              Eseguito
            </Badge>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 p-3">
            <div className="text-xs font-mono">
              {formatValue(testResult.output)}
            </div>
          </div>

          {/* JSON Collapsible per output completo */}
          <Collapsible>
            <CollapsibleTrigger className="flex items-center gap-2 w-full text-left px-1 py-1.5 hover:bg-muted/50 rounded transition-colors">
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform data-[state=open]:rotate-90" />
              <p className="text-xs font-medium text-muted-foreground">Vedi JSON completo</p>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="bg-muted/30 rounded-lg border p-3 mt-2">
                <pre className="text-[10px] font-mono overflow-auto max-h-64 text-foreground whitespace-pre-wrap">
                  {JSON.stringify(testResult.output, null, 2)}
                </pre>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </div>
  );
}

function AIVoiceCallConfig({
  config,
  onChange,
  nodeDefinition,
  availableVariables,
  models,
  modelsOffline,
}: {
  config: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  nodeDefinition: NodeDefinition | null;
  availableVariables?: AvailableVariable[];
  models?: {id:string;name:string}[];
  modelsOffline?: boolean;
}) {
  const properties = nodeDefinition?.input_schema?.properties || {};
  const compatibleVars = (availableVariables || []).map(v => ({
    path: v.path, type: v.type, context: v.context, source: v.source,
    source_node_id: v.source_node_id, source_node_label: v.source_node_label, description: v.description,
  }));

  const renderTemplateField = (key: string, label: string, placeholder?: string, description?: string) => {
    const schema = properties[key];
    if (!schema) return null;
    return (
      <ConfigFieldWithMode
        key={key}
        label={label}
        fieldKey={key}
        value={(config[key] as string | number) ?? schema.default ?? ''}
        onChange={(v) => onChange(key, v)}
        description={description || schema.description}
        placeholder={placeholder || schema.placeholder}
        availableVariables={compatibleVars}
        compatibleTypes={['string', 'any']}
        fieldContext={schema.context}
        inputType="text"
      />
    );
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50/50 dark:bg-emerald-950/20 p-4 space-y-3">
        <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
          <Phone className="h-4 w-4" />
          <span className="text-sm font-semibold">Destinazione & Agente</span>
        </div>
        {renderTemplateField('to', 'Numero Destinazione', '{{trigger.message.from}}')}
        <div className="space-y-1">
          <Label className="text-xs text-emerald-600 dark:text-emerald-400">Agente AI Vocale</Label>
          <ConfigFieldWithMode
            label=""
            fieldKey="agent_id"
            value={(config.agent_id as string | number) ?? ''}
            onChange={(v) => onChange('agent_id', v)}
            optionsSource={properties.agent_id?.optionsSource}
            availableVariables={compatibleVars}
            compatibleTypes={['string', 'any']}
            fieldContext="generic_string"
            inputType="text"
          />
        </div>
      </div>

      <div className="rounded-lg border border-blue-200 dark:border-blue-800/40 bg-blue-50/50 dark:bg-blue-950/20 p-4 space-y-3">
        <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
          <Volume2 className="h-4 w-4" />
          <span className="text-sm font-semibold">Voce & Modello</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs flex items-center gap-1"><Bot className="h-3 w-3" /> Modello AI</Label>
              {modelsOffline && (
                <span className="text-[10px] px-1.5 py-0 rounded border border-amber-300 bg-amber-50 text-amber-700" title="La lista modelli proviene dal fallback locale: la chiamata API al provider non è andata a buon fine.">⚠️ Lista offline — verifica chiave API</span>
              )}
            </div>
            <Select value={(config.model as string) || 'gpt-realtime-2'} onValueChange={(v) => onChange('model', v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent position="popper" className="z-[200]">
                {(models || []).map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1"><Volume2 className="h-3 w-3" /> Voce</Label>
            <Select value={(config.voice_id as string) || 'marin'} onValueChange={(v) => onChange('voice_id', v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent position="popper" className="z-[200]">
                <SelectItem value="marin">Marin (Premium)</SelectItem>
                <SelectItem value="cedar">Cedar (Premium - Calda)</SelectItem>
                <SelectItem value="alloy">Alloy (Neutrale)</SelectItem>
                <SelectItem value="ash">Ash (Calda)</SelectItem>
                <SelectItem value="ballad">Ballad (Espressiva)</SelectItem>
                <SelectItem value="coral">Coral (Amichevole)</SelectItem>
                <SelectItem value="echo">Echo (Profonda)</SelectItem>
                <SelectItem value="fable">Fable (Narrativa)</SelectItem>
                <SelectItem value="onyx">Onyx (Autoritaria)</SelectItem>
                <SelectItem value="nova">Nova (Giovane)</SelectItem>
                <SelectItem value="sage">Sage (Rassicurante)</SelectItem>
                <SelectItem value="shimmer">Shimmer (Vivace)</SelectItem>
                <SelectItem value="verse">Verse (Poetica)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Stile Espressivo</Label>
            <Select value={(config.expressive_style as string) || 'conversational'} onValueChange={(v) => onChange('expressive_style', v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent position="popper" className="z-[200]">
                <SelectItem value="conversational">Conversazionale</SelectItem>
                <SelectItem value="neutral">Neutro</SelectItem>
                <SelectItem value="expressive">Espressivo</SelectItem>
                <SelectItem value="calm">Calmo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Temperature</Label>
            <div className="flex items-center gap-1.5">
              <input type="range" min="0" max="2" step="0.1" value={(config.temperature as string | number) ?? 0.8} onChange={(e) => onChange('temperature', parseFloat(e.target.value))} className="flex-1 h-1.5 accent-blue-500" />
              <span className="text-[10px] font-mono w-6 text-right text-muted-foreground">{((config.temperature as number) ?? 0.8).toFixed(1)}</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Velocita Parlato</Label>
            <div className="flex items-center gap-1.5">
              <input type="range" min="0.5" max="1.5" step="0.05" value={(config.speaking_rate as string | number) ?? 0.9} onChange={(e) => onChange('speaking_rate', parseFloat(e.target.value))} className="flex-1 h-1.5 accent-blue-500" />
              <span className="text-[10px] font-mono w-7 text-right text-muted-foreground">{((config.speaking_rate as number) ?? 0.9).toFixed(2)}</span>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Stabilita Voce</Label>
            <div className="flex items-center gap-1.5">
              <input type="range" min="0" max="1" step="0.05" value={(config.stability as string | number) ?? 0.5} onChange={(e) => onChange('stability', parseFloat(e.target.value))} className="flex-1 h-1.5 accent-blue-500" />
              <span className="text-[10px] font-mono w-7 text-right text-muted-foreground">{((config.stability as number) ?? 0.5).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-violet-200 dark:border-violet-800/40 bg-violet-50/50 dark:bg-violet-950/20 p-4 space-y-3">
        <div className="flex items-center gap-2 text-violet-700 dark:text-violet-300">
          <MessageSquare className="h-4 w-4" />
          <span className="text-sm font-semibold">Prompt & Voce</span>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-violet-600 dark:text-violet-400 flex items-center gap-1"><Mic className="h-3 w-3" /> Istruzioni Vocali</Label>
          <textarea
            value={(config.voice_instructions as string | number) || ''}
            onChange={(e) => onChange('voice_instructions', e.target.value)}
            placeholder="Parla con tono professionale ma cordiale..."
            className={`w-full min-h-[50px] ${PROMPT_TEXTAREA_CLASS}`}
            rows={2}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-violet-600 dark:text-violet-400">System Prompt</Label>
          <textarea
            value={(config.system_prompt as string | number) || ''}
            onChange={(e) => onChange('system_prompt', e.target.value)}
            placeholder="Sei un assistente professionale..."
            className={`w-full min-h-[80px] ${PROMPT_TEXTAREA_CLASS}`}
            rows={3}
          />
        </div>
        {renderTemplateField('context_data', 'Contesto Aggiuntivo', '{{memory_search.memories}}')}
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <SlidersHorizontal className="h-4 w-4" />
          <span className="text-sm font-semibold">VAD & Avanzate</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Tipo VAD</Label>
            <Select value={(config.vad_type as string) || 'server_vad'} onValueChange={(v) => onChange('vad_type', v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent position="popper" className="z-[200]">
                <SelectItem value="server_vad">Server VAD (Classico)</SelectItem>
                <SelectItem value="semantic_vad">Semantic VAD (Intelligente)</SelectItem>
                <SelectItem value="none">Nessuno (Push-to-talk)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {config.vad_type === 'semantic_vad' && (
            <div className="space-y-1">
              <Label className="text-xs">Eagerness</Label>
              <Select value={(config.semantic_vad_eagerness as string) || 'auto'} onValueChange={(v) => onChange('semantic_vad_eagerness', v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent position="popper" className="z-[200]">
                  <SelectItem value="low">Basso (Paziente)</SelectItem>
                  <SelectItem value="medium">Medio</SelectItem>
                  <SelectItem value="high">Alto (Reattivo)</SelectItem>
                  <SelectItem value="auto">Automatico</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {(config.vad_type === 'server_vad' || !config.vad_type) && (
            <>
              <div className="space-y-1">
                <Label className="text-xs">Soglia</Label>
                <div className="flex items-center gap-1.5">
                  <input type="range" min="0" max="1" step="0.05" value={(config.vad_threshold as string | number) ?? 0.5} onChange={(e) => onChange('vad_threshold', parseFloat(e.target.value))} className="flex-1 h-1.5 accent-primary" />
                  <span className="text-[10px] font-mono w-6">{((config.vad_threshold as number) ?? 0.5).toFixed(2)}</span>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Prefix Pad (ms)</Label>
                <Input type="number" value={(config.vad_prefix_padding_ms as string | number) ?? 300} onChange={(e) => onChange('vad_prefix_padding_ms', parseInt(e.target.value))} className="h-7 text-xs" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Silenzio (ms)</Label>
                <Input type="number" value={(config.vad_silence_duration_ms as string | number) ?? 500} onChange={(e) => onChange('vad_silence_duration_ms', parseInt(e.target.value))} className="h-7 text-xs" />
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-3 p-2 rounded border border-gray-100 dark:border-gray-800">
          <Switch checked={(config.echo_cancellation as boolean) || false} onCheckedChange={(v) => onChange('echo_cancellation', v)} />
          <div>
            <Label className="text-xs">Echo Cancellation</Label>
            <p className="text-[10px] text-muted-foreground">Riduzione eco vivavoce</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Max Tokens</Label>
            <Input type="number" value={(config.max_response_tokens as string | number) ?? 4096} onChange={(e) => onChange('max_response_tokens', parseInt(e.target.value))} className="h-7 text-xs" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Cicli Tool</Label>
            <Input type="number" value={(config.max_tool_rounds as string | number) ?? 5} onChange={(e) => onChange('max_tool_rounds', parseInt(e.target.value))} className="h-7 text-xs" />
          </div>
        </div>
      </div>

      {properties.enabled_tools && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <MultiSelectField
            fieldKey="enabled_tools"
            label="Tools Abilitati"
            description={properties.enabled_tools.description}
            value={Array.isArray(config.enabled_tools) ? config.enabled_tools : []}
            onChange={(v) => onChange('enabled_tools', v)}
            optionsSource={properties.enabled_tools.optionsSource}
          />
        </div>
      )}
    </div>
  );
}

function AIAgentConfig({
  config,
  onChange,
  nodeDefinition,
  availableVariables,
}: {
  config: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  nodeDefinition: NodeDefinition | null;
  availableVariables?: AvailableVariable[];
}) {
  const properties = nodeDefinition?.input_schema?.properties || {};
  const compatibleVars = (availableVariables || []).map(v => ({
    path: v.path, type: v.type, context: v.context, source: v.source,
    source_node_id: v.source_node_id, source_node_label: v.source_node_label, description: v.description,
  }));

  return (
    <div className="space-y-4 min-w-0" style={{ maxWidth: '100%' }}>
      <div className="rounded-lg border border-violet-200 dark:border-violet-800/40 bg-violet-50/50 dark:bg-violet-950/20 p-4 space-y-3 min-w-0">
        <div className="flex items-center gap-2 text-violet-700 dark:text-violet-300">
          <MessageSquare className="h-4 w-4 shrink-0" />
          <span className="text-sm font-semibold">Prompt & Istruzioni</span>
        </div>
        <div className="space-y-1 min-w-0">
          <Label className="text-xs text-violet-600 dark:text-violet-400">Prompt (supporta variabili {"{{...}}"}) *</Label>
          <textarea
            value={(config.prompt as string | number) ?? ''}
            onChange={(e) => onChange('prompt', e.target.value)}
            placeholder="Analizza il seguente testo: {{trigger.message}}"
            className={`w-full min-h-[70px] ${PROMPT_TEXTAREA_CLASS}`}
            style={{ maxWidth: '100%', boxSizing: 'border-box' }}
            rows={3}
          />
        </div>
        <div className="space-y-1 min-w-0">
          <Label className="text-xs text-violet-600 dark:text-violet-400">System Prompt</Label>
          <textarea
            value={(config.system_prompt as string | number) || ''}
            onChange={(e) => onChange('system_prompt', e.target.value)}
            placeholder="Sei un assistente che analizza testi..."
            className={`w-full min-h-[60px] ${PROMPT_TEXTAREA_CLASS}`}
            style={{ maxWidth: '100%', boxSizing: 'border-box' }}
            rows={2}
          />
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <SlidersHorizontal className="h-4 w-4" />
          <span className="text-sm font-semibold">Configurazione Modello</span>
        </div>
        <LLMProviderConfig
          provider="openai"
          model={(config.model as string) || 'gpt-5.4-mini'}
          llmConfig={{}}
          compact
          fixedProvider="openai"
          hideProviderSelector
          onProviderChange={() => {}}
          onModelChange={(model) => onChange('model', model)}
          onConfigChange={() => {}}
        />
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Formato Output</Label>
            <Select value={(config.output_format as string) || 'text'} onValueChange={(v) => onChange('output_format', v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent position="popper" className="z-[200]">
                <SelectItem value="text">Testo</SelectItem>
                <SelectItem value="json">JSON</SelectItem>
                <SelectItem value="boolean">Booleano (true/false)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Temperature</Label>
            <div className="flex items-center gap-1.5">
              <input
                type="range" min="0" max="2" step="0.1"
                value={(config.temperature as string | number) ?? 0.7}
                onChange={(e) => onChange('temperature', parseFloat(e.target.value))}
                className="flex-1 h-1.5 accent-violet-500"
              />
              <span className="text-[10px] font-mono w-6 text-right text-muted-foreground">{((config.temperature as number) ?? 0.7).toFixed(1)}</span>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Max Tokens</Label>
            <Input type="number" value={(config.max_tokens as string | number) ?? 1000} onChange={(e) => onChange('max_tokens', parseInt(e.target.value))} className="h-8 text-xs" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Cicli Tool</Label>
            <Input type="number" value={(config.max_tool_rounds as string | number) ?? 5} onChange={(e) => onChange('max_tool_rounds', parseInt(e.target.value))} className="h-8 text-xs" min={1} max={10} />
          </div>
        </div>
      </div>

      {properties.enabled_tools && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <MultiSelectField
            fieldKey="enabled_tools"
            label="Tool Abilitati"
            description={properties.enabled_tools.description}
            value={Array.isArray(config.enabled_tools) ? config.enabled_tools : []}
            onChange={(v) => onChange('enabled_tools', v)}
            optionsSource={properties.enabled_tools.optionsSource}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Task #2685: risolve l'ID ai_agent dal `knowledge_base_id` del nodo Claude Agent,
 * specchio esatto di `resolveKBAgentId` nel workflow-engine backend. Le KB collegate
 * (condivise) hanno il prefisso `agent:`, gli agenti node-owned provisionati salvano
 * l'ID grezzo. Restituisce null quando nessun agente è ancora associato (mostra hint).
 */
function resolveClaudeAgentId(knowledgeBaseId: unknown): string | null {
  if (typeof knowledgeBaseId !== 'string' || knowledgeBaseId.length === 0) return null;
  return knowledgeBaseId.startsWith('agent:') ? knowledgeBaseId.slice('agent:'.length) : knowledgeBaseId;
}

function ClaudeAgentConfig({
  config,
  onChange,
  nodeDefinition,
  availableVariables,
}: {
  config: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  nodeDefinition: NodeDefinition | null;
  availableVariables?: AvailableVariable[];
}) {
  const properties = nodeDefinition?.input_schema?.properties || {};

  return (
    <div className="space-y-4 min-w-0" style={{ maxWidth: '100%' }}>
      <div className="rounded-lg border border-violet-200 dark:border-violet-800/40 bg-violet-50/50 dark:bg-violet-950/20 p-4 space-y-3 min-w-0">
        <div className="flex items-center gap-2 text-violet-700 dark:text-violet-300">
          <MessageSquare className="h-4 w-4 shrink-0" />
          <span className="text-sm font-semibold">Prompt & Istruzioni</span>
        </div>
        <div className="space-y-1 min-w-0">
          <Label className="text-xs text-violet-600 dark:text-violet-400">Prompt (supporta variabili {"{{...}}"}) *</Label>
          <textarea
            value={(config.prompt as string | number) ?? ''}
            onChange={(e) => onChange('prompt', e.target.value)}
            placeholder="Analizza il seguente testo: {{trigger.message}}"
            className={`w-full min-h-[70px] ${PROMPT_TEXTAREA_CLASS}`}
            style={{ maxWidth: '100%', boxSizing: 'border-box' }}
            rows={3}
          />
        </div>
        <div className="space-y-1 min-w-0">
          <Label className="text-xs text-violet-600 dark:text-violet-400">System Prompt</Label>
          <textarea
            value={(config.system_prompt as string | number) || ''}
            onChange={(e) => onChange('system_prompt', e.target.value)}
            placeholder="Sei un assistente esperto..."
            className={`w-full min-h-[60px] ${PROMPT_TEXTAREA_CLASS}`}
            style={{ maxWidth: '100%', boxSizing: 'border-box' }}
            rows={2}
          />
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <SlidersHorizontal className="h-4 w-4" />
          <span className="text-sm font-semibold">Configurazione Modello</span>
        </div>
        <LLMProviderConfig
          provider="claude"
          model={(config.model as string) || 'claude-sonnet-4-6'}
          llmConfig={{}}
          compact
          fixedProvider="claude"
          hideProviderSelector
          agentId={resolveClaudeAgentId(config.knowledge_base_id)}
          onProviderChange={() => {}}
          onModelChange={(model) => onChange('model', model)}
          onConfigChange={() => {}}
        />
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Formato Output</Label>
            <Select value={(config.output_format as string) || 'text'} onValueChange={(v) => onChange('output_format', v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent position="popper" className="z-[200]">
                <SelectItem value="text">Testo</SelectItem>
                <SelectItem value="json">JSON</SelectItem>
                <SelectItem value="boolean">Booleano (true/false)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Temperature</Label>
            <div className="flex items-center gap-1.5">
              <input type="range" min="0" max="1" step="0.1" value={(config.temperature as string | number) ?? 0.7} onChange={(e) => onChange('temperature', parseFloat(e.target.value))} className="flex-1 h-1.5 accent-violet-500" />
              <span className="text-[10px] font-mono w-6 text-right text-muted-foreground">{((config.temperature as number) ?? 0.7).toFixed(1)}</span>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Max Tokens</Label>
            <Input type="number" value={(config.max_tokens as string | number) ?? 4096} onChange={(e) => onChange('max_tokens', parseInt(e.target.value))} className="h-8 text-xs" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Cicli Tool</Label>
            <Input type="number" value={(config.max_tool_rounds as string | number) ?? 5} onChange={(e) => onChange('max_tool_rounds', parseInt(e.target.value))} className="h-8 text-xs" min={1} max={10} />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-amber-200 dark:border-amber-800/40 bg-amber-50/50 dark:bg-amber-950/20 p-4 space-y-3">
        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
          <Zap className="h-4 w-4" />
          <span className="text-sm font-semibold">Extended Thinking & Effort</span>
        </div>
        <div className="flex items-center gap-3 p-2 rounded border border-amber-100 dark:border-amber-800">
          <Switch checked={(config.thinking_enabled as boolean) || false} onCheckedChange={(v) => onChange('thinking_enabled', v)} />
          <div>
            <Label className="text-xs">Extended Thinking</Label>
            <p className="text-[10px] text-muted-foreground">Ragionamento interno per problemi complessi</p>
          </div>
        </div>
        {(config.thinking_enabled as string | number) && (
          <div className="space-y-1">
            <Label className="text-xs">Budget Thinking (tokens)</Label>
            <Input type="number" value={(config.thinking_budget as string | number) ?? 4096} onChange={(e) => onChange('thinking_budget', parseInt(e.target.value))} className="h-8 text-xs" min={1024} max={32768} />
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-xs">Effort</Label>
          <Select value={(config.effort as string) || 'none'} onValueChange={(v) => onChange('effort', v === 'none' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Default" /></SelectTrigger>
            <SelectContent position="popper" className="z-[200]">
              <SelectItem value="none">Default</SelectItem>
              <SelectItem value="low">Basso (Veloce)</SelectItem>
              <SelectItem value="medium">Medio</SelectItem>
              <SelectItem value="high">Alto (Massima qualita)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {properties.enabled_tools && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <MultiSelectField
            fieldKey="enabled_tools"
            label="Tool Abilitati"
            description={properties.enabled_tools.description}
            value={Array.isArray(config.enabled_tools) ? config.enabled_tools : []}
            onChange={(v) => onChange('enabled_tools', v)}
            optionsSource={properties.enabled_tools.optionsSource}
          />
        </div>
      )}
    </div>
  );
}

function GeminiAgentConfig({
  config,
  onChange,
  nodeDefinition,
  availableVariables,
}: {
  config: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  nodeDefinition: NodeDefinition | null;
  availableVariables?: AvailableVariable[];
}) {
  const properties = nodeDefinition?.input_schema?.properties || {};
  const selectedModel = (config.model as string | undefined) || 'gemini-2.5-flash';
  const isModel2x = selectedModel.startsWith('gemini-2.0') || selectedModel.startsWith('gemini-1.');
  const nativeTools: string[] = Array.isArray(config.native_tools) ? config.native_tools : [];
  const hasMapsTool = nativeTools.includes('google_maps');
  const hasCustomTools = Array.isArray(config.enabled_tools) && config.enabled_tools.length > 0;

  const handleNativeToolToggle = (toolId: string, checked: boolean) => {
    let updated: string[];
    if (toolId === 'google_maps') {
      updated = checked ? ['google_maps'] : [];
    } else {
      const current = nativeTools.filter(t => t !== 'google_maps');
      if (checked) { if (!current.includes(toolId)) current.push(toolId); updated = current; }
      else { updated = current.filter(t => t !== toolId); }
    }
    onChange('native_tools', updated);
  };

  return (
    <div className="space-y-4 min-w-0" style={{ maxWidth: '100%' }}>
      <div className="rounded-lg border border-violet-200 dark:border-violet-800/40 bg-violet-50/50 dark:bg-violet-950/20 p-4 space-y-3 min-w-0">
        <div className="flex items-center gap-2 text-violet-700 dark:text-violet-300">
          <MessageSquare className="h-4 w-4 shrink-0" />
          <span className="text-sm font-semibold">Prompt & Istruzioni</span>
        </div>
        <div className="space-y-1 min-w-0">
          <Label className="text-xs text-violet-600 dark:text-violet-400">Prompt (supporta variabili {"{{...}}"}) *</Label>
          <textarea
            value={(config.prompt as string | number) ?? ''}
            onChange={(e) => onChange('prompt', e.target.value)}
            placeholder="Analizza il seguente testo: {{trigger.message}}"
            className={`w-full min-h-[70px] ${PROMPT_TEXTAREA_CLASS}`}
            style={{ maxWidth: '100%', boxSizing: 'border-box' }}
            rows={3}
          />
        </div>
        <div className="space-y-1 min-w-0">
          <Label className="text-xs text-violet-600 dark:text-violet-400">System Prompt</Label>
          <textarea
            value={(config.system_prompt as string | number) || ''}
            onChange={(e) => onChange('system_prompt', e.target.value)}
            placeholder="Sei un assistente esperto..."
            className={`w-full min-h-[60px] ${PROMPT_TEXTAREA_CLASS}`}
            style={{ maxWidth: '100%', boxSizing: 'border-box' }}
            rows={2}
          />
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <SlidersHorizontal className="h-4 w-4" />
          <span className="text-sm font-semibold">Configurazione Modello</span>
        </div>
        <LLMProviderConfig
          provider="gemini"
          model={selectedModel}
          llmConfig={{}}
          compact
          fixedProvider="gemini"
          hideProviderSelector
          onProviderChange={() => {}}
          onModelChange={(model) => onChange('model', model)}
          onConfigChange={() => {}}
        />
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Formato Output</Label>
            <Select value={(config.output_format as string) || 'text'} onValueChange={(v) => onChange('output_format', v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent position="popper" className="z-[200]">
                <SelectItem value="text">Testo</SelectItem>
                <SelectItem value="json">JSON</SelectItem>
                <SelectItem value="boolean">Booleano (true/false)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Temperature</Label>
            <div className="flex items-center gap-1.5">
              <input type="range" min="0" max="2" step="0.1" value={(config.temperature as string | number) ?? 0.7} onChange={(e) => onChange('temperature', parseFloat(e.target.value))} className="flex-1 h-1.5 accent-violet-500" />
              <span className="text-[10px] font-mono w-6 text-right text-muted-foreground">{((config.temperature as number) ?? 0.7).toFixed(1)}</span>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Max Tokens</Label>
            <Input type="number" value={(config.max_tokens as string | number) ?? 4096} onChange={(e) => onChange('max_tokens', parseInt(e.target.value))} className="h-8 text-xs" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Cicli Tool</Label>
            <Input type="number" value={(config.max_tool_rounds as string | number) ?? 5} onChange={(e) => onChange('max_tool_rounds', parseInt(e.target.value))} className="h-8 text-xs" min={1} max={10} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Top P</Label>
            <div className="flex items-center gap-1.5">
              <input type="range" min="0" max="1" step="0.05" value={(config.top_p as string | number) ?? 0.95} onChange={(e) => onChange('top_p', parseFloat(e.target.value))} className="flex-1 h-1.5 accent-blue-500" />
              <span className="text-[10px] font-mono w-7 text-right text-muted-foreground">{((config.top_p as number) ?? 0.95).toFixed(2)}</span>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Top K</Label>
            <Input type="number" value={(config.top_k as string | number) ?? 40} onChange={(e) => onChange('top_k', parseInt(e.target.value))} className="h-8 text-xs" min={1} max={100} />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-blue-200 dark:border-blue-800/40 bg-blue-50/50 dark:bg-blue-950/20 p-4 space-y-3">
        <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
          <Globe className="h-4 w-4" />
          <span className="text-sm font-semibold">Tool Nativi Google</span>
        </div>

        {isModel2x && hasCustomTools && nativeTools.length > 0 && (
          <div className="flex items-start gap-2 p-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40">
            <Info className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <p className="text-[10px] text-amber-700 dark:text-amber-300">I modelli 2.x non supportano tool nativi e tool MCP/custom contemporaneamente. Usa un modello 2.5+ o superiore per combinarli.</p>
          </div>
        )}

        <div className="space-y-2">
          {[
            { id: 'google_search', label: 'Google Search (Grounding)', desc: 'Cerca informazioni aggiornate sul web', exclusive: false },
            { id: 'code_execution', label: 'Code Execution', desc: 'Esegue codice Python per calcoli e analisi', exclusive: false },
            { id: 'url_context', label: 'URL Context', desc: 'Analizza contenuto di pagine web', exclusive: false },
            { id: 'google_maps', label: 'Google Maps', desc: 'Cerca luoghi, indicazioni e informazioni geografiche', exclusive: true },
          ].map(tool => {
            const isDisabled = tool.exclusive ? false : hasMapsTool;
            const isChecked = nativeTools.includes(tool.id);
            return (
              <div key={tool.id} className={`flex items-center gap-3 p-2 rounded border ${isDisabled ? 'border-gray-200 dark:border-gray-800 opacity-50' : 'border-blue-100 dark:border-blue-800'}`}>
                <Switch
                  checked={isChecked}
                  disabled={isDisabled}
                  onCheckedChange={(checked) => handleNativeToolToggle(tool.id, checked)}
                />
                <div className="flex-1">
                  <Label className="text-xs">{tool.label}</Label>
                  <p className="text-[10px] text-muted-foreground">{tool.desc}</p>
                  {tool.exclusive && isChecked && (
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">Esclusivo: disabilita gli altri tool nativi quando attivo</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {properties.enabled_tools && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <MultiSelectField
            fieldKey="enabled_tools"
            label="Tool MCP Abilitati"
            description={properties.enabled_tools.description}
            value={Array.isArray(config.enabled_tools) ? config.enabled_tools : []}
            onChange={(v) => onChange('enabled_tools', v)}
            optionsSource={properties.enabled_tools.optionsSource}
          />
        </div>
      )}
    </div>
  );
}

function GeminiVoiceCallConfig({
  config,
  onChange,
  nodeDefinition,
  availableVariables,
  models,
  modelsOffline,
}: {
  config: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  nodeDefinition: NodeDefinition | null;
  availableVariables?: AvailableVariable[];
  models?: {id:string;name:string}[];
  modelsOffline?: boolean;
}) {
  const properties = nodeDefinition?.input_schema?.properties || {};
  const compatibleVars = (availableVariables || []).map(v => ({
    path: v.path, type: v.type, context: v.context, source: v.source,
    source_node_id: v.source_node_id, source_node_label: v.source_node_label, description: v.description,
  }));
  const nativeTools: string[] = Array.isArray(config.native_tools) ? config.native_tools : [];
  const hasMapsTool = nativeTools.includes('google_maps');

  const handleNativeToolToggle = (toolId: string, checked: boolean) => {
    let updated: string[];
    if (toolId === 'google_maps') {
      updated = checked ? ['google_maps'] : [];
    } else {
      const current = nativeTools.filter(t => t !== 'google_maps');
      if (checked) { if (!current.includes(toolId)) current.push(toolId); updated = current; }
      else { updated = current.filter(t => t !== toolId); }
    }
    onChange('native_tools', updated);
  };

  const renderTemplateField = (key: string, label: string, placeholder?: string, description?: string) => {
    const schema = properties[key];
    if (!schema) return null;
    return (
      <ConfigFieldWithMode
        key={key}
        label={label}
        fieldKey={key}
        value={(config[key] as string | number) ?? schema.default ?? ''}
        onChange={(v) => onChange(key, v)}
        description={description || schema.description}
        placeholder={placeholder || schema.placeholder}
        availableVariables={compatibleVars}
        compatibleTypes={['string', 'any']}
        fieldContext={schema.context}
        inputType="text"
      />
    );
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50/50 dark:bg-emerald-950/20 p-4 space-y-3">
        <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
          <Phone className="h-4 w-4" />
          <span className="text-sm font-semibold">Destinazione & Agente</span>
        </div>
        {renderTemplateField('to', 'Numero Destinazione', '{{trigger.message.from}}')}
        <div className="space-y-1">
          <Label className="text-xs text-emerald-600 dark:text-emerald-400">Agente Gemini Vocale</Label>
          <ConfigFieldWithMode
            label=""
            fieldKey="agent_id"
            value={(config.agent_id as string | number) ?? ''}
            onChange={(v) => onChange('agent_id', v)}
            optionsSource={properties.agent_id?.optionsSource}
            availableVariables={compatibleVars}
            compatibleTypes={['string', 'any']}
            fieldContext="generic_string"
            inputType="text"
          />
        </div>
      </div>

      <div className="rounded-lg border border-blue-200 dark:border-blue-800/40 bg-blue-50/50 dark:bg-blue-950/20 p-4 space-y-3">
        <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
          <Volume2 className="h-4 w-4" />
          <span className="text-sm font-semibold">Voce & Modello</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs flex items-center gap-1"><Bot className="h-3 w-3" /> Modello</Label>
              {modelsOffline && (
                <span className="text-[10px] px-1.5 py-0 rounded border border-amber-300 bg-amber-50 text-amber-700" title="La lista modelli proviene dal fallback locale: la chiamata API al provider non è andata a buon fine.">⚠️ Lista offline — verifica chiave API</span>
              )}
            </div>
            <Select value={(config.model as string) || 'gemini-2.5-flash-native-audio-latest'} onValueChange={(v) => onChange('model', v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent position="popper" className="z-[200]">
                {(models || [
                  { id: 'gemini-2.5-flash-native-audio-latest', name: 'Gemini 2.5 Flash Native Audio (Latest)' },
                  { id: 'gemini-2.5-flash-native-audio-preview-12-2025', name: 'Gemini 2.5 Flash Native Audio' },
                  { id: 'gemini-2.0-flash-live-001', name: 'Gemini 2.0 Flash Live' },
                ]).map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1"><Volume2 className="h-3 w-3" /> Voce</Label>
            <Select value={(config.voice as string) || 'Kore'} onValueChange={(v) => onChange('voice', v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent position="popper" className="z-[200]">
                <SelectItem value="Kore">Kore (Femminile, chiara)</SelectItem>
                <SelectItem value="Charon">Charon (Maschile, profonda)</SelectItem>
                <SelectItem value="Puck">Puck (Maschile, vivace)</SelectItem>
                <SelectItem value="Fenrir">Fenrir (Maschile, autoritaria)</SelectItem>
                <SelectItem value="Aoede">Aoede (Femminile, melodica)</SelectItem>
                <SelectItem value="Leda">Leda (Femminile, calda)</SelectItem>
                <SelectItem value="Orus">Orus (Maschile, rassicurante)</SelectItem>
                <SelectItem value="Zephyr">Zephyr (Neutrale, leggera)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Lingua</Label>
            <Select value={(config.language as string) || 'it-IT'} onValueChange={(v) => onChange('language', v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent position="popper" className="z-[200]">
                <SelectItem value="it-IT">Italiano</SelectItem>
                <SelectItem value="en-US">English</SelectItem>
                <SelectItem value="de-DE">Deutsch</SelectItem>
                <SelectItem value="fr-FR">Francais</SelectItem>
                <SelectItem value="es-ES">Espanol</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Temperature</Label>
            <div className="flex items-center gap-1.5">
              <input type="range" min="0" max="2" step="0.1" value={(config.temperature as string | number) ?? 0.7} onChange={(e) => onChange('temperature', parseFloat(e.target.value))} className="flex-1 h-1.5 accent-blue-500" />
              <span className="text-[10px] font-mono w-6 text-right text-muted-foreground">{((config.temperature as number) ?? 0.7).toFixed(1)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-violet-200 dark:border-violet-800/40 bg-violet-50/50 dark:bg-violet-950/20 p-4 space-y-3">
        <div className="flex items-center gap-2 text-violet-700 dark:text-violet-300">
          <MessageSquare className="h-4 w-4" />
          <span className="text-sm font-semibold">Prompt & Messaggio</span>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-violet-600 dark:text-violet-400">System Prompt</Label>
          <textarea
            value={(config.system_prompt as string | number) || ''}
            onChange={(e) => onChange('system_prompt', e.target.value)}
            placeholder="Sei un assistente vocale professionale..."
            className={`w-full min-h-[80px] ${PROMPT_TEXTAREA_CLASS}`}
            rows={3}
          />
        </div>
        {renderTemplateField('first_message', 'Messaggio Iniziale', 'Buongiorno, come posso aiutarla?')}
        {renderTemplateField('context_data', 'Contesto Aggiuntivo', '{{memory_search.memories}}')}
      </div>

      <div className="rounded-lg border border-blue-200 dark:border-blue-800/40 bg-blue-50/50 dark:bg-blue-950/20 p-4 space-y-3">
        <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
          <Globe className="h-4 w-4" />
          <span className="text-sm font-semibold">Tool Nativi Google</span>
        </div>
        <div className="space-y-2">
          {[
            { id: 'google_search', label: 'Google Search (Grounding)', desc: 'Cerca informazioni aggiornate durante la chiamata', exclusive: false },
            { id: 'code_execution', label: 'Code Execution', desc: 'Esegue codice Python per calcoli e analisi', exclusive: false },
            { id: 'google_maps', label: 'Google Maps', desc: 'Cerca luoghi, indicazioni e info geografiche', exclusive: true },
          ].map(tool => {
            const isDisabled = tool.exclusive ? false : hasMapsTool;
            const isChecked = nativeTools.includes(tool.id);
            return (
              <div key={tool.id} className={`flex items-center gap-3 p-2 rounded border ${isDisabled ? 'border-gray-200 dark:border-gray-800 opacity-50' : 'border-blue-100 dark:border-blue-800'}`}>
                <Switch
                  checked={isChecked}
                  disabled={isDisabled}
                  onCheckedChange={(checked) => handleNativeToolToggle(tool.id, checked)}
                />
                <div className="flex-1">
                  <Label className="text-xs">{tool.label}</Label>
                  <p className="text-[10px] text-muted-foreground">{tool.desc}</p>
                  {tool.exclusive && isChecked && (
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">Esclusivo: disabilita gli altri tool nativi quando attivo</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <SlidersHorizontal className="h-4 w-4" />
          <span className="text-sm font-semibold">Opzioni Avanzate</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Durata Max (sec)</Label>
            <Input type="number" value={(config.max_duration as string | number) ?? 900} onChange={(e) => onChange('max_duration', parseInt(e.target.value))} className="h-7 text-xs" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Cicli Tool</Label>
            <Input type="number" value={(config.max_tool_rounds as string | number) ?? 5} onChange={(e) => onChange('max_tool_rounds', parseInt(e.target.value))} className="h-7 text-xs" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-3 p-2 rounded border border-gray-100 dark:border-gray-800">
            <Switch checked={(config.affective_dialog as boolean) || false} onCheckedChange={(v) => onChange('affective_dialog', v)} />
            <div>
              <Label className="text-xs">Dialogo Affettivo</Label>
              <p className="text-[10px] text-muted-foreground">Riconosce emozioni utente</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-2 rounded border border-gray-100 dark:border-gray-800">
            <Switch checked={(config.proactive_audio as boolean) || false} onCheckedChange={(v) => onChange('proactive_audio', v)} />
            <div>
              <Label className="text-xs">Audio Proattivo</Label>
              <p className="text-[10px] text-muted-foreground">L'agente parla spontaneamente</p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-3 p-2 rounded border border-gray-100 dark:border-gray-800">
            <Switch checked={(config.input_transcription as boolean) ?? true} onCheckedChange={(v) => onChange('input_transcription', v)} />
            <div>
              <Label className="text-xs">Trascrizione Input</Label>
              <p className="text-[10px] text-muted-foreground">Trascrivi audio utente</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-2 rounded border border-gray-100 dark:border-gray-800">
            <Switch checked={(config.output_transcription as boolean) ?? true} onCheckedChange={(v) => onChange('output_transcription', v)} />
            <div>
              <Label className="text-xs">Trascrizione Output</Label>
              <p className="text-[10px] text-muted-foreground">Trascrivi audio agente</p>
            </div>
          </div>
        </div>
        <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <Label className="text-xs font-medium flex items-center gap-1"><Mic className="h-3 w-3" /> VAD (Voice Activity Detection)</Label>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Sensibilità Inizio</Label>
              <Select value={(config.vad_start_sensitivity as string) || 'START_SENSITIVITY_LOW'} onValueChange={(v) => onChange('vad_start_sensitivity', v)}>
                <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent position="popper" className="z-[200]">
                  <SelectItem value="START_SENSITIVITY_LOW">Bassa</SelectItem>
                  <SelectItem value="START_SENSITIVITY_MEDIUM">Media</SelectItem>
                  <SelectItem value="START_SENSITIVITY_HIGH">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Sensibilità Fine</Label>
              <Select value={(config.vad_end_sensitivity as string) || 'END_SENSITIVITY_LOW'} onValueChange={(v) => onChange('vad_end_sensitivity', v)}>
                <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent position="popper" className="z-[200]">
                  <SelectItem value="END_SENSITIVITY_LOW">Bassa</SelectItem>
                  <SelectItem value="END_SENSITIVITY_MEDIUM">Media</SelectItem>
                  <SelectItem value="END_SENSITIVITY_HIGH">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Prefix Padding (ms)</Label>
              <Input type="number" value={(config.vad_prefix_padding_ms as string | number) ?? 300} onChange={(e) => onChange('vad_prefix_padding_ms', parseInt(e.target.value))} className="h-7 text-xs" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Silenzio (ms)</Label>
              <Input type="number" value={(config.vad_silence_duration_ms as string | number) ?? 1000} onChange={(e) => onChange('vad_silence_duration_ms', parseInt(e.target.value))} className="h-7 text-xs" />
            </div>
          </div>
        </div>
      </div>

      {properties.enabled_tools && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <MultiSelectField
            fieldKey="enabled_tools"
            label="Tools MCP Abilitati"
            description={properties.enabled_tools.description}
            value={Array.isArray(config.enabled_tools) ? config.enabled_tools : []}
            onChange={(v) => onChange('enabled_tools', v)}
            optionsSource={properties.enabled_tools.optionsSource}
          />
        </div>
      )}
    </div>
  );
}

function WorkflowNodeKBSection({
  nodeId,
  nodeLabel,
  config,
  onConfigChange,
}: {
  nodeId: string;
  nodeLabel: string;
  config: Record<string, unknown>;
  onConfigChange: (key: string, value: unknown) => void;
}) {
  const { toast } = useToast();
  const currentKbId: string = (config.knowledge_base_id as string) || '';
  const isLinked = currentKbId.startsWith('agent:');
  // KB "posseduta" dal nodo = id grezzo (creato inline via /workflow-agent).
  // KB "collegata" = riuso di una KB condivisa del tenant, valore con prefisso `agent:`.
  const [mode, setMode] = useState<'new' | 'existing'>(isLinked ? 'existing' : 'new');
  const [ownedKbId, setOwnedKbId] = useState<string | null>(isLinked ? null : (currentKbId || null));
  const [saving, setSaving] = useState(false);
  const [kbOptions, setKbOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    if (!currentKbId.startsWith('agent:') && currentKbId && currentKbId !== ownedKbId) {
      setOwnedKbId(currentKbId);
    }
  }, [currentKbId, ownedKbId]);

  useEffect(() => {
    if (mode !== 'existing' || !config.rag_enabled) return;
    let cancelled = false;
    setLoadingOptions(true);
    (async () => {
      try {
        const res = await apiClient.request('/workflows/options/knowledge-bases?scope=all') as { options?: Array<{ value: string; label: string }> } | null;
        if (!cancelled) setKbOptions(res?.options ?? []);
      } catch {
        if (!cancelled) setKbOptions([]);
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, config.rag_enabled]);

  const displayOptions = useMemo(() => {
    if (isLinked && currentKbId && !kbOptions.some((o) => o.value === currentKbId)) {
      return [{ value: currentKbId, label: 'KB collegata (corrente)' }, ...kbOptions];
    }
    return kbOptions;
  }, [kbOptions, isLinked, currentKbId]);

  const handleSaveAgent = async () => {
    if (!nodeId) return;
    setSaving(true);
    try {
      const res = await apiClient.post<{ agent_id: string; created: boolean }>('/knowledge-base/workflow-agent', {
        node_id: nodeId,
        node_label: nodeLabel,
        system_prompt: config.system_prompt || config.prompt || '',
        model: config.model || 'gpt-5.4-mini',
        temperature: config.temperature ?? 0.7,
      });
      const agentId = res.data?.agent_id;
      if (agentId) {
        setOwnedKbId(agentId);
        onConfigChange('knowledge_base_id', agentId);
        toast({ title: 'Agent salvato', description: 'Ora puoi configurare la Knowledge Base' });
      }
    } catch {
      toast({ title: 'Errore', description: 'Impossibile salvare l\'agent', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Cleanup orfano: se l'utente abbandona una KB creata inline e mai popolata
  // (0 documenti, 0 URL) la elimina, per non lasciare agenti WF vuoti nel tenant.
  // NON tocca le KB condivise (collegate): quelle hanno il prefisso `agent:`.
  const maybeDeleteEmptyOwned = async (agentId: string) => {
    try {
      const [docsRes, urlsRes] = await Promise.all([
        apiClient.get<unknown[]>(`/knowledge-base/${agentId}/documents`),
        apiClient.get<unknown[]>(`/knowledge-base/${agentId}/urls`),
      ]);
      const docs = Array.isArray(docsRes.data) ? docsRes.data : [];
      const urls = Array.isArray(urlsRes.data) ? urlsRes.data : [];
      if (docs.length === 0 && urls.length === 0) {
        await apiClient.delete(`/knowledge-base/workflow-agent/${agentId}`);
      }
    } catch {
      // best-effort: se il cleanup fallisce non blocchiamo lo switch
    }
  };

  const switchToExisting = async () => {
    if (ownedKbId) await maybeDeleteEmptyOwned(ownedKbId);
    setOwnedKbId(null);
    onConfigChange('knowledge_base_id', '');
    setMode('existing');
  };

  const switchToNew = () => {
    // Scollega la KB condivisa: non eliminarla mai (è una sorgente del tenant).
    onConfigChange('knowledge_base_id', '');
    setMode('new');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700/50">
        <Switch
          checked={(config.rag_enabled as boolean) || false}
          onCheckedChange={(checked) => onConfigChange('rag_enabled', checked)}
        />
        <div>
          <Label>Abilita RAG (Knowledge Base)</Label>
          <p className="text-xs text-gray-400">L'AI consulta la KB su richiesta tramite il tool <code className="text-[10px]">query_knowledge_base</code> (RAG tool-based), non automaticamente</p>
        </div>
      </div>

      {(config.rag_enabled as string | number) && (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700/50 ml-4 border-l-2 border-blue-300">
          <Switch
            checked={(config.rag_synthesis_mode || 'native') === 'native'}
            onCheckedChange={(checked) => { onConfigChange('rag_synthesis_mode', checked ? 'native' : 'pre_synthesized'); onConfigChange('rag_inferential_enabled', !checked); }}
          />
          <div className="flex items-center gap-1.5">
            <Label>Sintesi Nativa</Label>
            <div className="relative group">
              <Info className="h-3.5 w-3.5 text-gray-400 cursor-help" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-64 p-2 bg-gray-900 text-white text-[10px] rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none">
                <p className="font-medium mb-1">ON: L'agente ragiona direttamente sui dati recuperati dalla Knowledge Base. Più veloce, più naturale, costo inferiore.</p>
                <p>OFF: I dati vengono prima sintetizzati da un modello ausiliario (GPT-5.4-mini) e poi passati all'agente. Più controllata, utile con modelli costosi.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {(config.rag_enabled as string | number) && (
        <div className="mt-4 space-y-3">
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 text-xs">
            <button
              type="button"
              onClick={() => { if (mode !== 'new') switchToNew(); }}
              className={`px-3 py-1.5 rounded-md transition-colors ${mode === 'new' ? 'bg-sky-500 text-white' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Crea KB per questo nodo
            </button>
            <button
              type="button"
              onClick={() => { if (mode !== 'existing') void switchToExisting(); }}
              className={`px-3 py-1.5 rounded-md transition-colors ${mode === 'existing' ? 'bg-sky-500 text-white' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Usa KB esistente
            </button>
          </div>

          {mode === 'new' ? (
            !ownedKbId ? (
              <div className="text-center py-6">
                <Database className="h-10 w-10 mx-auto mb-3 text-sky-500/60" />
                <p className="text-sm text-muted-foreground mb-4">
                  Salva la configurazione dell'agent per gestire documenti e URL della Knowledge Base
                </p>
                <Button onClick={handleSaveAgent} disabled={saving} size="sm" className="gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  {saving ? 'Salvataggio...' : 'Salva Agent e Abilita KB'}
                </Button>
              </div>
            ) : (
              <div>
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Database className="h-4 w-4 text-sky-500" />
                  Documenti & URL
                </h4>
                <KnowledgeBaseManager agentId={ownedKbId} compact />
              </div>
            )
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 dark:border-amber-800/40 bg-amber-50/50 dark:bg-amber-950/20">
                <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Stai collegando una KB condivisa del tenant. I contenuti vanno gestiti dalla sezione di origine (agente vocale, chatbot o nodo che la possiede): le modifiche impattano tutte le configurazioni che la usano.
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Knowledge Base esistente</Label>
                {loadingOptions ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Caricamento KB disponibili...
                  </div>
                ) : displayOptions.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2">
                    Nessuna KB popolata disponibile nel tenant. Passa a "Crea KB per questo nodo" oppure popola prima un agente vocale, un chatbot o un altro nodo.
                  </p>
                ) : (
                  <Select value={currentKbId || undefined} onValueChange={(v) => onConfigChange('knowledge_base_id', v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Seleziona una KB esistente" /></SelectTrigger>
                    <SelectContent position="popper" className="z-[200]">
                      {displayOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ConfigTab({ 
  nodeType, 
  config, 
  nodeDefinition,
  availableVariables,
  allNodes,
  onConfigChange,
  nodeId,
  nodeLabel,
  realtimeModels,
  realtimeModelsOffline,
  geminiVoiceModels,
  geminiVoiceModelsOffline,
}: { 
  nodeType: string; 
  config: Record<string, unknown>; 
  nodeDefinition: NodeDefinition | null;
  availableVariables?: AvailableVariable[];
  allNodes?: { id: string; label: string }[];
  onConfigChange: (key: string, value: unknown) => void;
  nodeId?: string;
  nodeLabel?: string;
  realtimeModels?: {id:string;name:string}[];
  realtimeModelsOffline?: boolean;
  geminiVoiceModels?: {id:string;name:string}[];
  geminiVoiceModelsOffline?: boolean;
}) {
  const [activeAITab, setActiveAITab] = useState<'settings' | 'knowledge'>('settings');

  const TriggerComponent = TRIGGER_CONFIG_COMPONENTS[nodeType];
  if (TriggerComponent) {
    return <TriggerComponent config={config} onChange={onConfigChange} availableVariables={availableVariables} />;
  }

  const FlowControlComponent = FLOW_CONTROL_CONFIG_COMPONENTS[nodeType];
  if (FlowControlComponent) {
    return <FlowControlComponent config={config} onChange={onConfigChange} availableVariables={availableVariables} allNodes={allNodes} />;
  }

  if (nodeType && nodeType.includes('CreateMessage') && nodeType.includes('Twilio')) {
    return (
      <TwilioCreateMessageConfig
        nodeDefinition={nodeDefinition}
        config={config}
        availableVariables={availableVariables}
        onChange={onConfigChange}
      />
    );
  }

  // eslint-disable-next-line voip/no-hardcoded-llm-model-list -- id di TIPO NODO workflow (claude_agent/gemini_agent...), non model id: falso positivo del pattern claude-/gemini-.
  const AI_NODE_TYPES = ['ai_agent', 'ai_voice_call', 'claude_agent', 'gemini_agent', 'gemini_voice_call'];
  if (AI_NODE_TYPES.includes(nodeType) && nodeId) {
    const renderAISettings = () => {
      switch (nodeType) {
        case 'ai_voice_call':
          return <AIVoiceCallConfig config={config} onChange={onConfigChange} nodeDefinition={nodeDefinition} availableVariables={availableVariables} models={realtimeModels} modelsOffline={realtimeModelsOffline} />;
        case 'claude_agent':
          return <ClaudeAgentConfig config={config} onChange={onConfigChange} nodeDefinition={nodeDefinition} availableVariables={availableVariables} />;
        case 'gemini_agent':
          return <GeminiAgentConfig config={config} onChange={onConfigChange} nodeDefinition={nodeDefinition} availableVariables={availableVariables} />;
        case 'gemini_voice_call':
          return <GeminiVoiceCallConfig config={config} onChange={onConfigChange} nodeDefinition={nodeDefinition} availableVariables={availableVariables} models={geminiVoiceModels} modelsOffline={geminiVoiceModelsOffline} />;
        default:
          return <AIAgentConfig config={config} onChange={onConfigChange} nodeDefinition={nodeDefinition} availableVariables={availableVariables} />;
      }
    };

    return (
      <div className="space-y-4 min-w-0 overflow-hidden">
        <div style={{ display: 'flex', width: '100%' }} className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <button
            type="button"
            onClick={() => setActiveAITab('settings')}
            style={{ width: '50%', flexShrink: 0 }}
            className={`flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold transition-colors ${
              activeAITab === 'settings'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted/40 text-muted-foreground hover:bg-muted'
            }`}
          >
            <Settings className="h-3.5 w-3.5" />
            Impostazioni
          </button>
          <button
            type="button"
            onClick={() => setActiveAITab('knowledge')}
            style={{ width: '50%', flexShrink: 0 }}
            className={`flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold transition-colors ${
              activeAITab === 'knowledge'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted/40 text-muted-foreground hover:bg-muted'
            }`}
          >
            <Database className="h-3.5 w-3.5" />
            Knowledge & RAG
          </button>
        </div>

        {activeAITab === 'settings' && renderAISettings()}

        {activeAITab === 'knowledge' && (
          <WorkflowNodeKBSection
            nodeId={nodeId}
            nodeLabel={nodeLabel || nodeId}
            config={config}
            onConfigChange={onConfigChange}
          />
        )}
      </div>
    );
  }

  return (
    <GenericSchemaConfig 
      nodeDefinition={nodeDefinition} 
      config={config} 
      availableVariables={availableVariables}
      onChange={onConfigChange} 
    />
  );
}

function GenericSchemaConfig({ 
  nodeDefinition, 
  config, 
  availableVariables,
  onChange 
}: { 
  nodeDefinition: NodeDefinition | null; 
  config: Record<string, unknown>; 
  availableVariables?: AvailableVariable[];
  onChange: (key: string, value: unknown) => void;
}) {
  if (!nodeDefinition?.input_schema?.properties || Object.keys(nodeDefinition.input_schema.properties).length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Settings className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>Nessuna configurazione richiesta per questo nodo.</p>
      </div>
    );
  }

  const properties = nodeDefinition?.input_schema?.properties || {};
  const required = nodeDefinition?.input_schema?.required || [];

  const getCompatibleTypes = (schemaType: string): string[] => {
    switch (schemaType) {
      case 'string': return ['string', 'any'];
      case 'number': 
      case 'integer': return ['number', 'integer', 'any'];
      case 'boolean': return ['boolean', 'any'];
      case 'array': return ['array', 'any'];
      case 'object': return ['object', 'any'];
      default: return ['any'];
    }
  };

  const getInputType = (schema: FieldSchema): 'text' | 'number' | 'textarea' => {
    if (schema.type === 'number' || schema.type === 'integer') return 'number';
    if (schema.format === 'textarea' || 
        schema.key?.includes('body') || 
        schema.key?.includes('template') || 
        schema.key?.includes('prompt')) return 'textarea';
    return 'text';
  };

  const inferFieldContext = (fieldKey: string, schema: FieldSchema): string | undefined => {
    if (schema.context) return schema.context;

    const key = fieldKey.toLowerCase();

    const exactMatches: Record<string, string> = {
      'phone': 'phone_number',
      'phone_number': 'phone_number',
      'caller_id': 'phone_number',
      'contact_phone': 'phone_number',
      'target_number': 'phone_number',
      'from_number': 'phone_number',
      'to_number': 'phone_number',
      'phone_to': 'phone_number',
      'phone_from': 'phone_number',

      'email': 'email',
      'email_address': 'email',
      'contact_email': 'email',
      'to_email': 'email',
      'from_email': 'email',
      'recipient_email': 'email',

      'extension': 'extension_number',
      'extension_number': 'extension_number',
      'target_extension': 'extension_number',
      'source_extension': 'extension_number',

      'trunk_id': 'trunk_id',
      'sip_trunk_id': 'trunk_id',
      'trunk_name': 'trunk_name',

      'call_id': 'call_id',
      'call_uuid': 'call_id',
      'channel_uuid': 'call_id',

      'lead_id': 'lead_id',
      'customer_id': 'customer_id',
      'person_id': 'person_id',
      'contact_id': 'customer_id',

      'pipeline_id': 'pipeline_id',
      'stage_id': 'stage_id',
      'campaign_id': 'campaign_id',
      'form_id': 'form_id',
      'tag_name': 'tag_name',

      'user_id': 'user_id',
      'agent_id': 'user_id',
      'assigned_to': 'user_id',
      'owner_id': 'user_id',

      'first_name': 'contact_name',
      'last_name': 'contact_name',
      'contact_name': 'contact_name',
      'company_name': 'contact_name',

      'sms_body': 'message_content',
      'whatsapp_body': 'message_content',
      'notification_message': 'message_content',
      'message_body': 'message_content',
      'message_text': 'message_content',

      'email_subject': 'email_subject',

      'template_id': 'template_id',
      'whatsapp_template_id': 'template_id',
      'content_sid': 'template_id',

      'conversation_id': 'conversation_id',
      'message_id': 'message_id',

      'webhook_url': 'url',
      'callback_url': 'url',
      'redirect_url': 'url',

      'workflow_id': 'workflow_id',

      'call_direction': 'call_direction',

      'scheduled_at': 'datetime',
      'due_date': 'datetime',
      'start_date': 'datetime',
      'end_date': 'datetime',

      'max_duration': 'duration_seconds',

      'vat_number': 'vat_number',
      'tax_code': 'tax_code',
      'fiscal_code': 'tax_code',
      'codice_fiscale': 'tax_code',
      'partita_iva': 'vat_number',

      'lead_source': 'lead_source',

      'system_prompt': 'message_content',
      'ai_prompt': 'message_content',
    };

    if (exactMatches[key]) return exactMatches[key];

    if (key.includes('phone') || key.includes('numero') || key.includes('cellulare') || key.includes('telefono')) return 'phone_number';
    if (key.includes('email') || key.includes('mail')) return 'email';
    if (key.includes('extension') || key.includes('interno')) return 'extension_number';
    if (key.includes('trunk')) return 'trunk_id';
    if (key.includes('call_id') || key.includes('uuid')) return 'call_id';
    if (key.includes('lead_id')) return 'lead_id';
    if (key.includes('customer_id')) return 'customer_id';
    if (key.includes('campaign')) return 'campaign_id';
    if (key.includes('pipeline')) return 'pipeline_id';
    if (key.includes('template')) return 'template_id';
    if (key.includes('user_id') || key.includes('agent_id')) return 'user_id';

    return undefined;
  };

  return (
    <div className="space-y-4">
      {Object.entries(properties).map(([key, schema]) => {
        const isRequired = required.includes(key);
        const value = config[key] ?? schema.default ?? '';
        const fieldLabel = schema.title || key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());

        if (schema.format === 'multi_select') {
          return (
            <MultiSelectField
              key={key}
              fieldKey={key}
              label={fieldLabel}
              description={schema.description}
              value={Array.isArray(value) ? value : []}
              onChange={(newValue) => onChange(key, newValue)}
              optionsSource={(schema.optionsSource as string)}
            />
          );
        }
        
        const options: FieldOption[] | undefined = schema.enum 
          ? schema.enum.map((opt: string) => ({ value: opt, label: opt }))
          : undefined;

        const compatibleVars: ConfigAvailableVariable[] = availableVariables 
          ? availableVariables.map(v => ({
              path: v.path,
              type: v.type,
              context: v.context,
              source: v.source,
              source_node_id: v.source_node_id,
              source_node_label: v.source_node_label,
              description: v.description
            }))
          : [];

        const resolvedContext = inferFieldContext(key, schema);

        return (
          <ConfigFieldWithMode
            key={key}
            label={fieldLabel}
            fieldKey={key}
            value={value as string | number}
            onChange={(newValue, mode) => onChange(key, newValue)}
            description={schema.description}
            format={schema.format}
            options={options}
            optionsSource={(schema.optionsSource as string)}
            availableVariables={compatibleVars}
            compatibleTypes={getCompatibleTypes(schema.type as string)}
            fieldContext={resolvedContext}
            allowAll={(schema.allowAll as boolean) || false}
            allLabel={(schema.allLabel as string) || 'Tutti'}
            placeholder={schema.placeholder || schema.description}
            inputType={getInputType(schema)}
            required={isRequired}
          />
        );
      })}
    </div>
  );
}

function MultiSelectField({
  fieldKey,
  label,
  description,
  value,
  onChange,
  optionsSource,
}: {
  fieldKey: string;
  label: string;
  description?: string;
  value: string[];
  onChange: (value: string[]) => void;
  optionsSource?: string;
}) {
  const [options, setOptions] = useState<Array<{ value: string; label: string; description?: string }>>([]);
  const [groups, setGroups] = useState<Array<{ category: string; label: string; icon: string; color: string; tools: Array<{ value: string; label: string; description: string }> }>>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!optionsSource) return;
    setLoading(true);
    const fetchOpts = async () => {
      try {
        // apiClient gestisce il prefix slug centralmente (no template /api/${slug}).
        const endpoint = optionsSource.startsWith('/api/') ? optionsSource.substring(4) : optionsSource;
        const res = await apiClient.get<{
          options?: Array<{ value: string; label: string; description?: string }>;
          groups?: Array<{ category: string; label: string; icon: string; color: string; tools: Array<{ value: string; label: string; description: string }> }>;
        }>(endpoint);
        if (res.success && res.data) {
          setOptions(res.data.options || []);
          if (res.data.groups) {
            setGroups(res.data.groups);
            setExpandedGroups(new Set(res.data.groups.map((g) => g.category)));
          }
        }
      } catch (error) {
        console.error('Error loading multi-select options from', optionsSource, error);
      } finally {
        setLoading(false);
      }
    };
    fetchOpts();
  }, [optionsSource]);

  const toggleValue = (v: string) => {
    if (value.includes(v)) {
      onChange(value.filter(x => x !== v));
    } else {
      onChange([...value, v]);
    }
  };

  const toggleGroup = (groupTools: Array<{ value: string }>) => {
    const allSelected = groupTools.every(t => value.includes(t.value));
    if (allSelected) {
      onChange(value.filter(v => !groupTools.some(t => t.value === v)));
    } else {
      const newValues = [...new Set([...value, ...groupTools.map(t => t.value)])];
      onChange(newValues);
    }
  };

  const query = searchQuery.toLowerCase();

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</Label>
          {value.length > 0 && (
            <Badge variant="secondary" className="text-xs">{value.length} selezionati</Badge>
          )}
        </div>
        {description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="w-3.5 h-3.5 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-sm p-3">
                <p className="text-sm text-popover-foreground">{description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 p-3 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Caricamento...
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-md">
          {(options.length > 8 || groups.length > 0) && (
            <div className="p-2 border-b border-gray-200 dark:border-gray-700">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca tool..."
                className="h-7 text-xs"
              />
            </div>
          )}
          <div className="max-h-48 overflow-y-auto p-2 space-y-1">
            {groups.length > 0 ? (
              groups.map(group => {
                const filteredTools = group.tools.filter(t =>
                  !query || t.label.toLowerCase().includes(query) || t.value.toLowerCase().includes(query) || t.description?.toLowerCase().includes(query)
                );
                if (filteredTools.length === 0) return null;
                const allGroupSelected = filteredTools.every(t => value.includes(t.value));
                const someGroupSelected = filteredTools.some(t => value.includes(t.value));
                const isExpanded = expandedGroups.has(group.category);

                return (
                  <Collapsible key={group.category} open={isExpanded} onOpenChange={() => {
                    const next = new Set(expandedGroups);
                    isExpanded ? next.delete(group.category) : next.add(group.category);
                    setExpandedGroups(next);
                  }}>
                    <div className="flex items-center gap-2">
                      <CollapsibleTrigger className="flex-1 flex items-center gap-2 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
                        <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{group.label}</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0">{filteredTools.length}</Badge>
                      </CollapsibleTrigger>
                      <button
                        type="button"
                        className="text-[10px] text-blue-500 hover:text-blue-600 px-1.5"
                        onClick={() => toggleGroup(filteredTools)}
                      >
                        {allGroupSelected ? 'Deseleziona' : 'Tutti'}
                      </button>
                    </div>
                    <CollapsibleContent>
                      <div className="ml-5 space-y-0.5">
                        {filteredTools.map(tool => (
                          <label
                            key={tool.value}
                            className="flex items-start gap-2 p-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={value.includes(tool.value)}
                              onChange={() => toggleValue(tool.value)}
                              className="mt-0.5 rounded border-gray-300"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{tool.label}</div>
                              {tool.description && (
                                <div className="text-[10px] text-gray-500 truncate">{tool.description}</div>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })
            ) : (
              options.filter(o => !query || o.label.toLowerCase().includes(query) || o.value.toLowerCase().includes(query))
                .map(opt => (
                  <label
                    key={opt.value}
                    className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={value.includes(opt.value)}
                      onChange={() => toggleValue(opt.value)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300">{opt.label}</span>
                  </label>
                ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Special component for Twilio CreateMessage with Body/Template toggle
function TwilioCreateMessageConfig({
  nodeDefinition,
  config,
  availableVariables,
  onChange,
}: {
  nodeDefinition: NodeDefinition | null;
  config: Record<string, unknown>;
  availableVariables?: AvailableVariable[];
  onChange: (key: string, value: unknown) => void;
}) {
  const [messageMode, setMessageMode] = useState<'body' | 'template'>(
    config.ContentSid ? 'template' : 'body'
  );
  const [templates, setTemplates] = useState<MessagingWorkflowTemplateOption[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    setTemplateError(null);
    try {
      const res = await apiClient.get<MessagingWorkflowTemplateOption[]>('/messaging/templates/for-workflows');
      if (!res.success) {
        setTemplateError(res.error || 'Errore nel caricamento template');
        return;
      }
      setTemplates(res.data ?? []);
    } catch (err) {
      console.error('Failed to load templates:', err);
      setTemplateError(getErrorMessage(err) || 'Errore di connessione al server');
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const handleModeChange = (newMode: 'body' | 'template') => {
    setMessageMode(newMode);
    if (newMode === 'body') {
      onChange('ContentSid', '');
      onChange('ContentVariables', '');
    } else {
      onChange('Body', '');
    }
  };

  const handleTemplateSelect = (contentSid: string) => {
    onChange('ContentSid', contentSid);
    const template = templates.find(t => t.content_sid === contentSid);
    if (template?.variables && template.variables.length > 0) {
      const varsObj: Record<string, string> = {};
      template.variables.forEach((_v, i: number) => {
        varsObj[(i + 1).toString()] = '';
      });
      onChange('ContentVariables', JSON.stringify(varsObj));
    }
  };

  const compatibleVars: ConfigAvailableVariable[] = availableVariables
    ? availableVariables.map(v => ({
        path: v.path,
        type: v.type,
        source: v.source,
        source_node_id: v.source_node_id,
        source_node_label: v.source_node_label,
        description: v.description,
      }))
    : [];

  const selectedTemplate = templates.find(t => t.content_sid === config.ContentSid);

  return (
    <div className="space-y-4">
      {/* To field - always required */}
      <ConfigFieldWithMode
        label="Destinatario (To)"
        fieldKey="To"
        value={(config.To as string | number) || ''}
        onChange={(val) => onChange('To', val)}
        tooltip="Numero destinatario in formato whatsapp:+39XXXXXXXXXX o E.164 per SMS"
        availableVariables={compatibleVars}
        compatibleTypes={['string', 'any']}
        placeholder="whatsapp:{{trigger.caller_id_number}}"
        inputType="text"
        required
      />

      {/* From field - always required */}
      <ConfigFieldWithMode
        label="Mittente (From)"
        fieldKey="From"
        value={(config.From as string | number) || ''}
        onChange={(val) => onChange('From', val)}
        tooltip="Numero Twilio mittente in formato whatsapp:+39XXXXXXXXXX"
        availableVariables={compatibleVars}
        compatibleTypes={['string', 'any']}
        placeholder="whatsapp:+393928941931"
        inputType="text"
        required
      />

      {/* Mode toggle */}
      <div className="border rounded-lg p-4 bg-muted/30">
        <Label className="text-sm font-medium mb-3 block">Tipo di messaggio</Label>
        <div className="flex gap-2">
          <Button
            variant={messageMode === 'body' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleModeChange('body')}
            className="flex-1"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            Testo libero
          </Button>
          <Button
            variant={messageMode === 'template' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleModeChange('template')}
            className="flex-1"
          >
            <FileText className="h-4 w-4 mr-2" />
            Template WhatsApp
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          {messageMode === 'body' 
            ? 'Il testo libero funziona solo entro 24h dall\'ultima risposta del cliente'
            : 'I template approvati da Meta funzionano sempre, anche fuori dalla finestra 24h'}
        </p>
      </div>

      {/* Body input (freeform mode) */}
      {messageMode === 'body' && (
        <ConfigFieldWithMode
          label="Testo del messaggio"
          fieldKey="Body"
          value={(config.Body as string | number) || ''}
          onChange={(val) => onChange('Body', val)}
          tooltip="Contenuto del messaggio (max 1600 caratteri)"
          availableVariables={compatibleVars}
          compatibleTypes={['string', 'any']}
          placeholder="Ciao {{trigger.caller_id_name}}, grazie per averci contattato!"
          inputType="textarea"
          required
        />
      )}

      {/* Template selection (template mode) */}
      {messageMode === 'template' && (
        <>
          <div>
            <Label className="text-sm font-medium mb-2 block">Template WhatsApp</Label>
            {loadingTemplates ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Caricamento template...
              </div>
            ) : templateError ? (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg border border-destructive/30">
                <Info className="h-4 w-4 inline mr-2" />
                {templateError}
              </div>
            ) : templates.length === 0 ? (
              <div className="text-sm text-muted-foreground bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
                <Info className="h-4 w-4 inline mr-2" />
                Nessun template WhatsApp disponibile. Crea e approva template nella sezione Messaggistica.
              </div>
            ) : (
              <Select
                value={(config.ContentSid as string) || ''}
                onValueChange={handleTemplateSelect}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona un template approvato" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.content_sid} value={template.content_sid}>
                      <div className="flex flex-col">
                        <span className="font-medium">{template.name}</span>
                        <span className="text-xs text-muted-foreground truncate max-w-[300px]">
                          {template.body_text}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Show template preview */}
          {selectedTemplate && (
            <div className="bg-muted/50 p-3 rounded-lg border">
              <Label className="text-xs text-muted-foreground mb-1 block">Anteprima template</Label>
              <p className="text-sm">{selectedTemplate.body_text}</p>
              {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                <div className="mt-2 pt-2 border-t">
                  <Label className="text-xs text-muted-foreground mb-2 block">
                    Variabili del template ({selectedTemplate.variables.length})
                  </Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Usa ContentVariables JSON per popolare le variabili
                  </p>
                  <ConfigFieldWithMode
                    label="ContentVariables (JSON)"
                    fieldKey="ContentVariables"
                    value={(config.ContentVariables as string | number) || ''}
                    onChange={(val) => onChange('ContentVariables', val)}
                    tooltip='JSON con le variabili del template, es: {"1": "Mario", "2": "ordine #123"}'
                    availableVariables={compatibleVars}
                    compatibleTypes={['string', 'any']}
                    placeholder='{"1": "{{trigger.caller_id_name}}"}'
                    inputType="textarea"
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function getTriggerExplanation(nodeType: string): string {
  const explanations: Record<string, string> = {
    trigger_manual: 'Questo trigger permette di avviare il workflow manualmente. Puoi farlo cliccando un pulsante nell\'interfaccia o chiamando l\'API. Utile per workflow che devono essere eseguiti su richiesta.',
    trigger_scheduled: 'Questo trigger esegue il workflow automaticamente secondo una programmazione. Puoi impostare esecuzioni giornaliere, settimanali, mensili o con qualsiasi frequenza usando le espressioni cron.',
    trigger_webhook: 'Questo trigger riceve richieste HTTP POST da servizi esterni. Quando un servizio invia dati al tuo webhook, il workflow si attiva automaticamente. Perfetto per integrazioni con sistemi esterni.',
    trigger_call_hangup: 'Questo trigger si attiva quando una chiamata telefonica termina. Puoi filtrare per direzione (entrante/uscente) e per esito (risposta, non risposta, occupato).',
    trigger_call_answered: 'Questo trigger si attiva nel momento in cui il destinatario risponde a una chiamata. Utile per avviare azioni in tempo reale durante le chiamate.',
    trigger_voicemail_left: 'Questo trigger si attiva quando qualcuno lascia un messaggio vocale. Puoi filtrare per interni specifici.',
    trigger_sms_received: 'Questo trigger si attiva quando ricevi un SMS su uno dei tuoi numeri. Puoi filtrare per numero destinatario specifico.',
    trigger_whatsapp_received: 'Questo trigger si attiva quando ricevi un messaggio WhatsApp. Puoi filtrare per numero destinatario specifico.',
    trigger_lead_created: 'Questo trigger si attiva quando viene creato un nuovo lead nel CRM. Puoi filtrare per pipeline o sorgente.',
    trigger_lead_stage_changed: 'Questo trigger si attiva quando un lead cambia stage nella pipeline. Perfetto per automazioni basate sul progresso del lead.',
    trigger_customer_created: 'Questo trigger si attiva quando viene creato un nuovo cliente nel CRM. Puoi filtrare per tipo (B2C privati o B2B aziende).',
    trigger_form_submitted: 'Questo trigger si attiva quando qualcuno compila un form embed. Include automaticamente i parametri UTM per il tracking.',
    trigger_tag_added: 'Questo trigger si attiva quando viene aggiunto un tag a un lead o cliente. Utile per automazioni basate sulla classificazione.',
    trigger_message_delivered: 'Questo trigger si attiva quando un messaggio SMS o WhatsApp viene consegnato con successo al destinatario.',
    trigger_message_failed: 'Questo trigger si attiva quando l\'invio di un messaggio SMS o WhatsApp fallisce. Utile per gestire errori e retry.',
    trigger_message_read: 'Questo trigger si attiva quando il destinatario legge un messaggio WhatsApp. Disponibile solo per WhatsApp.',
    trigger_contact_status_changed: 'Questo trigger si attiva quando un contatto di una campagna cambia stato. Puoi filtrare per campagna e stato specifico.',
    trigger_time_in_status: 'Questo trigger si attiva quando un contatto rimane in uno stato per un certo periodo di tempo. Perfetto per follow-up automatici.',
  };
  return explanations[nodeType] || 'Questo nodo avvia il workflow quando si verifica un evento specifico.';
}

function getOutputVariables(nodeType: string): { name: string; type: string; description: string }[] {
  const outputs: Record<string, { name: string; type: string; description: string }[]> = {
    trigger_manual: [
      { name: 'input', type: 'object', description: 'Dati passati manualmente all\'avvio' },
      { name: 'triggered_by', type: 'string', description: 'ID dell\'utente che ha avviato il workflow' },
      { name: 'triggered_at', type: 'date', description: 'Data e ora di avvio' },
    ],
    trigger_scheduled: [
      { name: 'scheduled_at', type: 'date', description: 'Orario schedulato per l\'esecuzione' },
      { name: 'execution_count', type: 'number', description: 'Numero di volte che è stato eseguito' },
    ],
    trigger_webhook: [
      { name: 'headers', type: 'object', description: 'Headers HTTP della richiesta' },
      { name: 'body', type: 'object', description: 'Corpo della richiesta (JSON)' },
      { name: 'query', type: 'object', description: 'Parametri query string (?key=value)' },
      { name: 'method', type: 'string', description: 'Metodo HTTP (POST, PUT, etc.)' },
    ],
    trigger_call_hangup: [
      { name: 'call_uuid', type: 'string', description: 'Identificativo univoco della chiamata' },
      { name: 'caller', type: 'string', description: 'Numero del chiamante' },
      { name: 'callee', type: 'string', description: 'Numero del destinatario' },
      { name: 'duration', type: 'number', description: 'Durata della chiamata in secondi' },
      { name: 'disposition', type: 'string', description: 'Esito (answered, no_answer, busy)' },
      { name: 'direction', type: 'string', description: 'Direzione (inbound, outbound)' },
      { name: 'recording_url', type: 'string', description: 'URL della registrazione (se disponibile)' },
    ],
    trigger_call_answered: [
      { name: 'call_uuid', type: 'string', description: 'Identificativo univoco della chiamata' },
      { name: 'caller', type: 'string', description: 'Numero del chiamante' },
      { name: 'callee', type: 'string', description: 'Numero del destinatario' },
      { name: 'direction', type: 'string', description: 'Direzione (inbound, outbound)' },
    ],
    trigger_voicemail_left: [
      { name: 'voicemail_id', type: 'string', description: 'ID del messaggio vocale' },
      { name: 'caller', type: 'string', description: 'Numero di chi ha lasciato il messaggio' },
      { name: 'extension', type: 'string', description: 'Interno del destinatario' },
      { name: 'duration', type: 'number', description: 'Durata del messaggio in secondi' },
      { name: 'audio_url', type: 'string', description: 'URL per ascoltare l\'audio' },
      { name: 'transcription', type: 'string', description: 'Trascrizione testuale (se disponibile)' },
    ],
    trigger_sms_received: [
      { name: 'from', type: 'string', description: 'Numero del mittente' },
      { name: 'to', type: 'string', description: 'Numero del destinatario' },
      { name: 'body', type: 'string', description: 'Testo del messaggio' },
      { name: 'message_sid', type: 'string', description: 'ID univoco Twilio del messaggio' },
    ],
    trigger_whatsapp_received: [
      { name: 'from', type: 'string', description: 'Numero del mittente' },
      { name: 'to', type: 'string', description: 'Numero del destinatario' },
      { name: 'body', type: 'string', description: 'Testo del messaggio' },
      { name: 'media_url', type: 'string', description: 'URL del media allegato (se presente)' },
      { name: 'message_sid', type: 'string', description: 'ID univoco Twilio del messaggio' },
    ],
    trigger_lead_created: [
      { name: 'lead', type: 'object', description: 'Oggetto lead completo' },
      { name: 'lead.id', type: 'string', description: 'ID univoco del lead' },
      { name: 'lead.first_name', type: 'string', description: 'Nome del lead' },
      { name: 'lead.last_name', type: 'string', description: 'Cognome del lead' },
      { name: 'lead.contact_phone', type: 'string', description: 'Telefono del lead' },
      { name: 'lead.contact_email', type: 'string', description: 'Email del lead' },
      { name: 'lead.company_name', type: 'string', description: 'Azienda del lead' },
      { name: 'lead.source', type: 'string', description: 'Sorgente di acquisizione' },
    ],
    trigger_lead_stage_changed: [
      { name: 'lead', type: 'object', description: 'Oggetto lead completo' },
      { name: 'old_stage', type: 'object', description: 'Stage precedente' },
      { name: 'new_stage', type: 'object', description: 'Nuovo stage' },
      { name: 'changed_by', type: 'string', description: 'ID utente che ha effettuato il cambio' },
    ],
    trigger_customer_created: [
      { name: 'customer', type: 'object', description: 'Oggetto cliente completo' },
      { name: 'customer.id', type: 'string', description: 'ID univoco del cliente' },
      { name: 'customer.first_name', type: 'string', description: 'Nome del cliente' },
      { name: 'customer.last_name', type: 'string', description: 'Cognome del cliente' },
      { name: 'customer.phone', type: 'string', description: 'Telefono del cliente' },
      { name: 'customer.email', type: 'string', description: 'Email del cliente' },
      { name: 'customer.type', type: 'string', description: 'Tipo (B2C o B2B)' },
    ],
    trigger_form_submitted: [
      { name: 'form_data', type: 'object', description: 'Tutti i campi compilati nel form' },
      { name: 'form_id', type: 'string', description: 'ID del form compilato' },
      { name: 'utm_source', type: 'string', description: 'Sorgente UTM (tracking)' },
      { name: 'utm_medium', type: 'string', description: 'Medium UTM (tracking)' },
      { name: 'utm_campaign', type: 'string', description: 'Campagna UTM (tracking)' },
      { name: 'referrer', type: 'string', description: 'Pagina di provenienza' },
    ],
    trigger_tag_added: [
      { name: 'entity', type: 'object', description: 'Oggetto lead o cliente a cui è stato aggiunto il tag' },
      { name: 'entity_type', type: 'string', description: 'Tipo di entità (lead o customer)' },
      { name: 'tag_name', type: 'string', description: 'Nome del tag aggiunto' },
      { name: 'added_by', type: 'string', description: 'ID utente che ha aggiunto il tag' },
    ],
    trigger_message_delivered: [
      { name: 'message_sid', type: 'string', description: 'ID univoco del messaggio' },
      { name: 'to_number', type: 'string', description: 'Numero destinatario' },
      { name: 'from_number', type: 'string', description: 'Numero mittente' },
      { name: 'channel', type: 'string', description: 'Canale (sms o whatsapp)' },
      { name: 'status', type: 'string', description: 'Stato del messaggio' },
      { name: 'timestamp', type: 'date', description: 'Data/ora di consegna' },
    ],
    trigger_message_failed: [
      { name: 'message_sid', type: 'string', description: 'ID univoco del messaggio' },
      { name: 'to_number', type: 'string', description: 'Numero destinatario' },
      { name: 'from_number', type: 'string', description: 'Numero mittente' },
      { name: 'channel', type: 'string', description: 'Canale (sms o whatsapp)' },
      { name: 'error_code', type: 'string', description: 'Codice errore Twilio' },
      { name: 'error_message', type: 'string', description: 'Descrizione errore' },
    ],
    trigger_message_read: [
      { name: 'message_sid', type: 'string', description: 'ID univoco del messaggio' },
      { name: 'to_number', type: 'string', description: 'Numero destinatario' },
      { name: 'from_number', type: 'string', description: 'Numero mittente' },
      { name: 'read_at', type: 'date', description: 'Data/ora di lettura' },
    ],
    trigger_contact_status_changed: [
      { name: 'contact_id', type: 'string', description: 'ID del contatto' },
      { name: 'campaign_id', type: 'string', description: 'ID della campagna' },
      { name: 'campaign_name', type: 'string', description: 'Nome della campagna' },
      { name: 'from_status', type: 'string', description: 'Stato precedente' },
      { name: 'to_status', type: 'string', description: 'Nuovo stato' },
      { name: 'phone', type: 'string', description: 'Numero di telefono' },
    ],
    trigger_time_in_status: [
      { name: 'contact_id', type: 'string', description: 'ID del contatto' },
      { name: 'campaign_id', type: 'string', description: 'ID della campagna' },
      { name: 'status', type: 'string', description: 'Stato attuale' },
      { name: 'hours_elapsed', type: 'number', description: 'Ore trascorse nello stato' },
      { name: 'status_since', type: 'date', description: 'Data/ora inizio stato' },
      { name: 'phone', type: 'string', description: 'Numero di telefono' },
    ],
  };
  return outputs[nodeType] || [];
}

function generateMockOutput(nodeType: string): Record<string, unknown> {
  const mockData: Record<string, Record<string, unknown>> = {
    trigger_manual: {
      input: { action: 'test', data: { key: 'value' } },
      triggered_by: 'user_123',
      triggered_at: new Date().toISOString(),
    },
    trigger_scheduled: {
      scheduled_at: new Date().toISOString(),
      execution_count: 42,
    },
    trigger_webhook: {
      headers: { 'content-type': 'application/json', 'x-api-key': 'abc123' },
      body: { event: 'order.created', order_id: 'ORD-12345' },
      query: { source: 'shopify' },
      method: 'POST',
    },
    trigger_call_hangup: {
      call_uuid: 'call-uuid-abc123',
      caller: '+393201234567',
      callee: '+390212345678',
      duration: 185,
      disposition: 'answered',
      direction: 'inbound',
      recording_url: 'https://storage.example.com/recordings/abc123.mp3',
    },
    trigger_call_answered: {
      call_uuid: 'call-uuid-def456',
      caller: '+393201234567',
      callee: '+390212345678',
      direction: 'inbound',
    },
    trigger_voicemail_left: {
      voicemail_id: 'vm-789',
      caller: '+393201234567',
      extension: '2001',
      duration: 45,
      audio_url: 'https://storage.example.com/voicemail/vm-789.mp3',
      transcription: 'Buongiorno, la chiamo per avere informazioni sul vostro servizio...',
    },
    trigger_sms_received: {
      from: '+393201234567',
      to: '+393339876543',
      body: 'Vorrei confermare l\'appuntamento di domani alle 15:00',
      message_sid: 'SM1234567890abcdef',
    },
    trigger_whatsapp_received: {
      from: '+393201234567',
      to: '+393339876543',
      body: 'Salve, ho bisogno di assistenza',
      media_url: null,
      message_sid: 'WA1234567890abcdef',
    },
    trigger_lead_created: {
      lead: {
        id: 'lead-abc123',
        first_name: 'Mario',
        last_name: 'Rossi',
        contact_phone: '+393201234567',
        contact_email: 'mario.rossi@example.com',
        company_name: 'Rossi SRL',
        source: 'website',
      },
    },
    trigger_lead_stage_changed: {
      lead: { id: 'lead-abc123', first_name: 'Mario', last_name: 'Rossi' },
      old_stage: { id: 'stage-1', name: 'Nuovo' },
      new_stage: { id: 'stage-2', name: 'Contattato' },
      changed_by: 'user_456',
    },
    trigger_customer_created: {
      customer: {
        id: 'cust-xyz789',
        first_name: 'Giulia',
        last_name: 'Bianchi',
        phone: '+393339876543',
        email: 'giulia.bianchi@example.com',
        type: 'B2C',
      },
    },
    trigger_form_submitted: {
      form_data: { name: 'Luigi Verdi', email: 'luigi@example.com', message: 'Interessato ai vostri servizi' },
      form_id: 'form-contact-01',
      utm_source: 'google',
      utm_medium: 'cpc',
      utm_campaign: 'brand_2025',
      referrer: 'https://www.google.com',
    },
    trigger_tag_added: {
      entity: { id: 'lead-abc123', first_name: 'Mario', last_name: 'Rossi' },
      entity_type: 'lead',
      tag_name: 'VIP',
      added_by: 'user_789',
    },
  };
  return mockData[nodeType] || { message: 'Mock data for this node type' };
}
